import { useTranslation } from 'react-i18next'
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { ChevronDown, Folder, PanelRight } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { toast } from 'sonner'
import { mutate } from 'swr'
import {
  AnalystChartSpec,
  AnalystTableSpec,
  AssistantContextUsage,
  AssistantCreativeDraft,
  AssistantEffort,
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
  SocialTrackerProposal,
  ThreadArtifactDto,
  WebCitation,
  WorkflowTemplate,
  isWorkflowRunTerminal,
} from '@digital-twins/shared-types'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ChatFileUpload } from '@/components/chat/ChatFileUpload'
import { AttachmentTiles } from '@/components/chat/AttachmentTiles'
import { useChatFileIngest } from '@/components/chat/useChatFileIngest'
import {
  hasStimulusStillHosting,
  isVideoChatImage,
  uploadHostedChatStimulus,
} from '@/components/chat/stimulus-videos'
import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import { composerTriggerClassName } from '@/components/composer/composerAppearance'
import { ContextRing } from '@/components/composer/ContextRing'
import { EffortToggle } from '@/components/composer/EffortToggle'
import { ScopeChip } from '@/components/composer/ScopeChip'
import { PageContent } from '@/components/layout/PageContent'
import {
  QuickStudyCutPlotsContext,
  quickStudyCutPlotsSourceFor,
} from '@/components/quick-study-results/quickStudyCutPlots'
import { PageLayout } from '@/components/layout/PageLayout'
import { upsertSidebarThread } from '@/components/layout/useSidebarThreads'
import {
  asAnalystCharts,
  asAnalystTables,
  ThreadFeaturedExhibits,
} from '@/components/quick-study-results/featuredPlots/ThreadFeaturedExhibits'
import { hasStudyDocEmbeds } from '@/components/study-doc'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/config/auth-provider'
import { cn } from '@/lib/utils'
import { templateSurface } from '@/lib/workflows/templateSurface'
import { getThreadPlaceholders } from '@/lib/constants/languages'
import {
  assistantTurnStream,
  AssistantTurnDoneFrame,
  ChatImage,
  knowledgeApi,
  knowledgeThreadsApi,
  KnowledgeThreadMessage,
  KnowledgeThreadView,
  AssistantTurnPlanTail,
  threadAssetsApi,
  ThreadAssetView,
} from '@/lib/api'
import { defaultContentLanguageFromUi } from '@/i18n'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'
import { useWorkflowRun } from '@/lib/workflows/useWorkflowRun'
import {
  connectWebSocket,
  onKnowledgeThreadMessageAdded,
  subscribeToKnowledgeThread,
  unsubscribeFromKnowledgeThread,
} from '@/lib/services/websocket'
import { ActiveWorkflowChip } from '@/pages/HomeLabPage/ActiveWorkflowChip'
import { WorkflowMenu } from '@/pages/HomeLabPage/WorkflowMenu'
import { useStartWorkflowRun } from '@/pages/WorkflowsPage/useStartWorkflowRun'
import { useWorkflowScheduleAudiences } from '@/pages/WorkflowsPage/useWorkflowScheduleAudiences'
import { workflowRunSegmentFieldsForSlug } from '@/pages/WorkflowsPage/workflowRunSegmentFields'
import { useWorkflowTemplate } from '@/pages/WorkflowsPage/useWorkflowTemplate'
import { useWorkflowTemplates } from '@/pages/WorkflowsPage/useWorkflowTemplates'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import {
  KnowledgeScopePicker,
  KnowledgeScopeSelection,
} from '../ThreadsPage/KnowledgeScopePicker'
import { scopeChipLabel } from '../ThreadsPage/threadHelpers'
import { documentTitle } from '../KnowledgeHubPage/libraryHelpers'
import { ThreadRunGate } from './ThreadRunGate'
import { ThreadRunActivityIndicator } from './ThreadRunActivityIndicator'
import { settledStepRefreshSignature } from './deriveThreadRunActivityLabel'
import { cutGroupsFromRun, ThreadRunResults } from './ThreadRunResults'
import { artifactDisplayLabel } from './ThreadRunPane/artifactDisplayLabel'
import {
  GateRevisionHandle,
  pendingGateDocksOnComposer,
} from './ThreadRunPane/gateArtifacts'
import {
  collectThreadArtifacts,
  countPendingCreativeDrafts,
  enrichStimulusMedia,
  filterFoldedStimulusItems,
  foldAssetTokensFromCreativeDtos,
  isLivingArtifactMutationTool,
  STUDY_DOC_SLUG,
  ThreadArtifactItem,
} from './ThreadRunPane/collectThreadArtifacts'
import { useThreadArtifactTips } from './ThreadRunPane/useThreadArtifactTips'
import { useThreadLivingArtifacts } from './ThreadRunPane/useThreadLivingArtifacts'
import { ThreadInlineArtifacts } from './ThreadRunPane/ArtifactPreviewCard'
import { ThreadArtifactExpandedView } from './ThreadRunPane/ThreadArtifactExpandedView'
import { ThreadArtifactFullscreenView } from './ThreadRunPane/ThreadArtifactFullscreenView'
import { ThreadArtifactPane } from './ThreadRunPane/ThreadArtifactPane'
import { ThreadCommandMenu, ThreadCommandSeed } from './ThreadCommandMenu'
import {
  hasStimulusGateSatisfied,
  hideFirstOptionalTextOnCard,
} from './ThreadCommandInputs'
import { hasBoundStimulusValue } from './ThreadStimulusInput'
import { buildPlanWorkflowCommandSeed } from './buildPlanWorkflowCommandSeed'
import { singleStepWorkflowSlug } from './singleStepWorkflowSlug'
import { audienceNameForInputs } from './audienceNameForInputs'
import {
  buildIntakeStartLatchKey,
  loadArmedWorkflowState,
  useArmedWorkflowState,
} from './useArmedWorkflowState'
import { audienceInputKey, composeRunStudyDoc } from './composeRunStudyDoc'
import {
  composeThreadExhibits,
  useThreadExhibitRuns,
} from './composeThreadExhibits'
import { useRunResults } from './useRunResults'
import { useRunTemplate } from './useRunTemplate'
import { studyPinAfterIndex } from './studyPinAnchor'
import {
  collectWorkflowRunStepNotes,
  currentAttemptWorkflowRunStepNotes,
} from './workflowRunStepNotes'
import {
  isPlainCreativeConfirm,
  isPlainGateContinue,
  isPlainGo,
  resolveCreativeDraftConfirmTarget,
  resolveGoTarget,
} from './threadGoIntent'
import { readOnlyThreadNotice } from './threadReadOnlyNotice'
import { matchesTypedCommand, ThreadCommand, useThreadCommands } from './useThreadCommands'
import {
  lastUserTurnId,
  originatingCommandUserTurnId,
  resolveWorkflowRunStartAt,
  userTurnAudienceChipLabel,
  userTurnCommandChipLabel,
} from './userTurnCommandChip'
import {
  findExpandableThreadArtifact,
  findThreadArtifactBySearchKey,
  readThreadArtifactKey,
  writeThreadArtifactSearchParam,
} from './threadArtifactSearch'
import { ThreadAnswerBody } from './ThreadAnswerBody'
import {
  ThreadCreativeDraftSet,
  toPreviewItem,
} from './ThreadCreativeDraftSet'
import { ThreadSocialTrackerCard } from './ThreadSocialTrackerCard'
import { ThreadTrackerProvenance } from './ThreadTrackerProvenance'
import { useSocialTrackers } from '../KnowledgeHubPage/social/useSocialTrackers'
import { ThreadIntakeStartActions } from './ThreadIntakeStartActions'
import { ThreadPlanCard } from './ThreadPlanCard'
import {
  stepHandoffFrom,
  useThreadPlanExecution,
} from './useThreadPlanExecution'
import {
  ThreadPlanProposal,
  ThreadPlanStepHandoff,
  ThreadPlanStepProposal,
} from './threadPlan.types'
import {
  ThreadActivityIndicator,
  ThreadActivityLeg,
} from './ThreadActivityIndicator'
import { ThreadSourcePanel, ThreadSourceTab } from './ThreadSourcePanel'
import {
  DRAWER_TRANSITION_MS,
  ThreadSourcesDrawer,
} from './ThreadSourcesDrawer'
import { ThreadUnplacedSources } from './ThreadUnplacedSources'
import { injectCitationMarkers, isCitationUuid } from './injectCitationMarkers'
import { groupCitationsByDocument } from './groupCitationsByDocument'
import { partitionAssistantCitations } from './partitionAssistantCitations'
import { referencesTurn } from './referencesTurn'
import {
  isWorkflowRunAskId,
  mergeRunCompletionAnnouncements,
} from './mergeRunCompletionAnnouncements'
import {
  isWorkflowRunCompletionAskId,
  WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX,
} from './workflowRunCompletion'
import { withRunCommandChip } from './withRunCommandChip'
import { workflowRunAskTurnAskId } from './workflowRunAskTurnAskId'

interface ThreadLocationState {
  pendingMessage?: string
  /* Home Lab's Ask composer carries its effort pick across the hand-off the same
     optional, omit-when-absent way it carries `pendingMessage` (see
     `useHomeLabSubmit.ts`'s own `pendingEffort` comment). Frontend review SHOULD FIX 2:
     this key existed on the write side already; this interface and the effect below are
     the read side that was missing. */
  pendingEffort?: AssistantEffort
  /**
   * Lobby example-prompt publicId (R3.6). Already stamped on the thread row at
   * create; kept here so the hand-off stays inspectable without a second fetch.
   */
  sourcePromptId?: string
  scope?: KnowledgeScopeSelection
}

interface LocalTurn {
  id: string
  role: 'user' | 'assistant'
  text: string
  createdAt?: string
  /**
   * Command name stamped at send when this turn launched an armed workflow
   * (`/test` intake or a direct start). Survives later follow-ups in the
   * same session; reload reconstructs it from the run via
   * `userTurnCommandChipLabel`.
   */
  commandDisplayName?: string
  /**
   * The audience that command was bound to, stamped beside
   * `commandDisplayName` (plan 2026-09-14-twin-test-bound-audience, B4). A
   * SECOND chip, never a rename of the first. Reload reconstructs it from the
   * run through `withRunCommandChip`, which takes the name this page resolved
   * because the run readback carries the id alone.
   */
  commandAudienceName?: string
  streaming?: boolean
  refused?: boolean
  stopped?: boolean
  gaps?: Array<string>
  /**
   * DOCUMENT citations only, already split out of the mixed
   * `KnowledgeAskCitation | AssistantCitation` array the server persists. Every
   * document renderer downstream (`groupCitationsByDocument`,
   * `injectCitationMarkers`, `ThreadAnswerBody`, `ThreadSourcePanel`) takes
   * this field unchanged, which is exactly why the web half is separated
   * before it ever reaches one of them.
   */
  citations?: Array<KnowledgeAskCitation>
  /**
   * The `kind: 'web'` half of the same array. Never anchored inline: a web
   * citation carries no char offsets, so it is disclosed as its own card under
   * the prose instead.
   */
  webCitations?: Array<WebCitation>
  /**
   * `judgment` frame notes for this turn, in arrival order. One quiet note
   * under the answer marking where the model went past its sources.
   */
  judgmentNotes?: Array<string>
  /**
   * Retrieval legs this turn announced, in `tool_start` order, each flipped to
   * `done` by its own `tool_end`.
   *
   * Read only while the turn is still waiting for its first token: it is what
   * `ThreadActivityIndicator` says out loud instead of guessing from a clock.
   * Absent is a real state and the reason this is optional: the legacy grounded
   * ask below emits no tool frames at all, and an intake turn plans no legs.
   */
  activityLegs?: Array<ThreadActivityLeg>
  /**
   * Agent C: validated plan proposal from the done-tail / message reload.
   * Only cardEligible (≥2 steps) plans are attached; the card mounts from
   * `useThreadPlanExecution` after `registerProposal`.
   */
  plan?: AssistantTurnPlanTail
  /**
   * Hosted composer attachments shown on the user bubble. Live turns copy
   * send-time tiles; reload joins GET-assets via `messageToken` (`id` is the
   * message token on rehydrate).
   */
  attachments?: Array<{ src: string; mimeType?: string; label?: string }>
  /** Persisted ask id; used to tag the durable run-completion announcement. */
  askId?: string | null
  /**
   * The plan card this turn was produced FOR, when the turn is a plan step's
   * own answer. Local only: it says which plan a bare "go" is still answering
   * once the step's prose is the newest thing on screen.
   */
  planMessageId?: string
  /**
   * The server token for a turn that was sent from this page, learned from the
   * `done` frame. A locally sent turn keeps its `newId()` id as its identity
   * for the whole session, because the plan cursor, the streaming updates and
   * the park effect all key off that id and renaming it mid-flight would
   * detach a plan card from its own message. This is the SECOND name for the
   * same turn, carried in case a later reload needs to recognise it by token
   * rather than id.
   *
   * Today's only consumer, `mergeRunCompletionAnnouncements`, first filters
   * incoming turns down to the durable run-completion announcement (its own
   * `askId`). That announcement is inserted directly by
   * `WorkflowRunCompletionListener`, an event-emitter callback that never runs
   * inside this page's own request/response cycle, so its message never
   * travels through a `done` frame this page receives: no locally sent turn
   * can ever carry a `serverToken` equal to one. The token match there is
   * defensive plumbing, kept for a future consumer of that merge that appends
   * non-announcement messages by token instead of id.
   *
   * Absent on a turn that arrived from the server (its `id` IS the token) and
   * on a turn whose `done` frame carried no tokens, an older backend.
   */
  serverToken?: string
  /** Featured analyst specs; rendered under assistant prose when present. */
  charts?: Array<AnalystChartSpec>
  tables?: Array<AnalystTableSpec>
  /**
   * Unconfirmed creative set from the done tail / message reload.
   * Confirm writes living rows from these bytes; the model is not on that path.
   */
  creativeDrafts?: Array<AssistantCreativeDraft>
  /** Tracker the agent suggested; the card creates it only on the client's click. */
  socialTrackerProposal?: SocialTrackerProposal
}

/** Agent C: map a card-eligible done-tail / message plan into D's proposal shape. */
function planTailToProposal(
  messageId: string,
  plan: AssistantTurnPlanTail,
): ThreadPlanProposal | null {
  if (!plan.cardEligible || plan.steps.length < 2) {
    return null
  }
  return {
    messageId,
    steps: plan.steps.map((step) => {
      const mapped: ThreadPlanStepProposal = {
        slug: step.slug,
        kind: step.kind,
        displayName: step.displayName,
        flags: step.flags,
      }
      if (step.title?.trim()) {
        mapped.title = step.title.trim()
      }
      const audience = step.inputs?.audience?.trim()
      if (audience) {
        mapped.inputs = { audience }
      }
      return mapped
    }),
  }
}

/**
 * Room left above the answer when a question is scrolled to the top of the
 * conversation: enough for the question bubble itself plus breathing space.
 */
const QUESTION_HEADROOM_PX = 140
/**
 * How long after parking the newest question a scroller width change still
 * re-parks it. Long enough to cover the run pane opening on a command send
 * (same tick to a few hundred ms); short enough that a later resize is the
 * reader's own business.
 */
const REPARK_AFTER_PARK_MS = 1500

/**
 * The thread body is a constant width in every state: loading, loaded, rail
 * open, rail closed. A conditional shell width is what made the page shrink
 * then grow on open. The rail animates inside this row instead.
 */
const THREAD_PAGE_MAX_WIDTH_CLASS = 'max-w-7xl'
/** Reading measure. Constant too: the rail shifts the column, never narrows it. */
const THREAD_COLUMN_CLASS = 'max-w-3xl'
/**
 * The page is a column: a full-bleed header over a measured body. The header
 * spans the viewport rather than the body width, so the title and the scope
 * chip sit at the page edges while the conversation stays on its measure.
 */
const THREAD_PAGE_SHELL_CLASS = 'flex h-full w-full flex-col gap-0'
/**
 * The header is a fixed 56px band, not padding around variable content: its
 * centre line has to land on the sidebar toggle glyph (rail `py-2` + a 40px
 * button = a centre at 28px), and `py-3` around a 26px chip put the row 3px
 * high.
 */
const THREAD_HEADER_CLASS =
  'flex h-14 w-full shrink-0 items-center justify-between gap-3 px-4 md:px-6'
const THREAD_BODY_ROW_CLASS =
  'mx-auto flex min-h-0 w-full flex-1 flex-row gap-4 px-4 pb-2 md:gap-6 md:px-8 md:pb-4'
/**
 * The row under the header is full-bleed so the sources pane can sit against
 * the right edge of the viewport, the way the Quick Study composition drawer
 * does. Only the reading area inside it is held to `max-w-7xl`; a pane docked
 * inside that measure reads as a floating card, not as part of the window.
 */
const THREAD_PAGE_ROW_CLASS = 'flex min-h-0 w-full flex-1 flex-row'

/**
 * How long the drawer takes to animate shut. Owned by ThreadSourcesDrawer,
 * which sets both the clip and the slide: the content stays mounted for exactly
 * this long on close, so the drawer clips real content on the way out instead
 * of animating an empty gap.
 */
const RAIL_TRANSITION_MS = DRAWER_TRANSITION_MS
const RAIL_OPEN_STORAGE_KEY = 'thread-detail:sources-rail-open'

function ThreadDetailLoadingShell() {
  return (
    <div
      className="flex min-w-0 flex-1 flex-col"
      data-testid="thread-detail-loading"
    >
      <div
        className={cn(
          'relative mx-auto flex min-h-0 w-full flex-1 flex-col',
          THREAD_COLUMN_CLASS,
        )}
        data-testid="thread-reading-column"
      >
        <Skeleton className="min-h-0 flex-1 rounded-2xl" />
        <div className="absolute inset-x-0 bottom-0 pt-8">
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  )
}

/**
 * Rail preference. Every localStorage touch is guarded: `window.localStorage`
 * is undefined in this repo's jsdom test env and throws in private browsing.
 * Pattern copied from `quickStudyPure.ts` readPersistedCompositionPaneWidth.
 */
function readPersistedRailOpen(): boolean {
  try {
    return localStorage.getItem(RAIL_OPEN_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function persistRailOpen(open: boolean): void {
  try {
    localStorage.setItem(RAIL_OPEN_STORAGE_KEY, String(open))
  } catch {
    /* private browsing / no storage */
  }
}

function newId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * The key of the input a settled intake direction fills: the template's FIRST
 * `text` input, which is the same one the composer already borrows its
 * placeholder from and the same one `useStartWorkflowRun` would have mapped a
 * message onto.
 *
 * Derived from the template rather than spelled `'ask'` here, so this page
 * carries no template's vocabulary. Undefined for a template that declares no
 * text input, and the direction is simply not sent.
 */
function askInputKey(template: WorkflowTemplate): string | undefined {
  return template.inputs.find((spec) => spec.type === 'text')?.key
}

/**
 * The reader's own words for a just-armed prepared run, when the armed
 * template turns out to be the composer-owned-optional-ask kind (D3,
 * twin-test-question-shape-and-no-command-path plan, Step 4). `pending` is
 * `pendingPreparedReaderAskRef`'s snapshot from the prepared-run seed site:
 * `null` for a typed `/test` arm and for the approved-plan "go ahead" path,
 * both of which keep reading the seeded `rest` value at the call site.
 *
 * Checked here rather than at the seed site because the template's shape
 * (`hideFirstOptionalTextOnCard`, the same check the confirm card uses to
 * hide that field) is only known once the confirm card has fetched the full
 * template — which is exactly the `template` `handleArmCommand` receives.
 */
function preparedReaderAsk(
  template: WorkflowTemplate,
  inputs: Record<string, string | null>,
  pending: { slug: string; message: string; rest: string } | null,
): string | null {
  if (!pending || pending.slug !== template.slug) {
    return null
  }
  /* The card being armed must be THAT prepared card: its hidden ask input
     still carries the `rest` the seed put there. A typed `/test` armed later
     for the same slug carries its own words (or nothing) and keeps them. */
  const seededRest = inputs[askInputKey(template) ?? ''] ?? ''
  if (seededRest !== pending.rest) {
    return null
  }
  return hideFirstOptionalTextOnCard(template.inputs) ? pending.message : null
}

function settledAudienceId(
  template: WorkflowTemplate | null | undefined,
  inputs: Record<string, string | null>,
): string | null {
  const key = template ? audienceInputKey(template) : 'audience'
  const raw = key ? inputs[key] : inputs.audience
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null
  }
  return raw.trim()
}

/**
 * Stand-in audience for a report-kind intake, which has no audience input to
 * settle: report mode reads the thread, never a panel.
 *
 * The auto-start gate is a three-part tuple (thread, direction, audience) and
 * a report brief still needs its own latch, so this fills the third slot with
 * a value no audience id can collide with rather than making the gate itself
 * nullable.
 */
const REPORT_INTAKE_SETTLED_AUDIENCE = 'report'

/**
 * What a settled report intake hands the writing turn.
 *
 * An OBJECT, not a bare string, on purpose. The scoping tail carries the
 * agreed brief today (`intake.ask`), and a structured coverage list (topics,
 * evidence ids, cuts) is an open proposal against the same tail. Shaped this
 * way, that field would ride ALONGSIDE the brief rather than replace it, and
 * neither `confirmIntakeDirection` nor the turn itself has to be reshaped to
 * carry it. Nothing here assumes `ask` is the only payload the tail can have.
 */
interface SettledReportScope {
  /** `intake.ask` verbatim: the agreed brief, and the turn's message. */
  brief: string
}

/**
 * The SERVER's own sentence out of a failed report turn.
 *
 * `assistantTurnStream` throws with the raw response body, so Nest's D13
 * refusal ("Nothing to report yet…") arrives wrapped in its JSON envelope.
 * That copy is the whole point of the refusal, so it is unwrapped and shown
 * verbatim; anything that is not that envelope is shown as thrown.
 */
function reportTurnErrorMessage(error: unknown, fallback: string): string {
  const raw =
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? (error instanceof Error ? error.message : '')
  const text = typeof raw === 'string' ? raw.trim() : ''
  if (!text) {
    return fallback
  }
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: unknown }
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim()
      }
    } catch {
      return text
    }
  }
  return text
}

const THREAD_ASSET_REGISTER_ATTEMPTS = 3
/** Ordinary turns reject empty `message`; attachment-only send uses this. */
const ATTACHMENT_ONLY_ASK = 'Please look at the attached.'

function hostableKindFromChatImage(
  image: ChatImage,
): 'image' | 'video' | null {
  if (isVideoChatImage(image)) {
    return 'video'
  }
  const mime = (image.mimeType ?? '').toLowerCase()
  if (mime.startsWith('image/')) {
    return 'image'
  }
  return null
}

function chatImagesToTurnAttachments(
  images: Array<ChatImage>,
): Array<{ src: string; mimeType?: string; label?: string }> {
  const items: Array<{ src: string; mimeType?: string; label?: string }> = []
  for (const image of images) {
    if (!image.url) {
      continue
    }
    items.push({
      src: image.url,
      mimeType: image.mimeType,
      label: image.label,
    })
  }
  return items
}

function listedAssetFileId(asset: ThreadAssetView): string | null {
  const fileId = asset.ref.fileId ?? asset.ref.id
  return fileId || null
}

function turnAttachmentsFromAssets(
  assets: Array<ThreadAssetView>,
  messageToken: string,
): Array<{ src: string; mimeType?: string; label?: string }> | undefined {
  const items: Array<{ src: string; mimeType?: string; label?: string }> = []
  for (const asset of assets) {
    if (asset.messageToken !== messageToken) {
      continue
    }
    const src = asset.ref.publicUrl
    if (!src) {
      continue
    }
    items.push({
      src,
      mimeType: asset.ref.mimeType,
      label: asset.ref.label,
    })
  }
  return items.length > 0 ? items : undefined
}

/**
 * Step whose settling turned `previous` into `next` (`settledStepRefreshSignature`,
 * a `stepId:state` list). The LAST new entry, so a burst that settles two steps
 * scrolls to the one the reader is furthest from. Null when nothing was added.
 */
function newestSettledStepId(previous: string, next: string): string | null {
  const seen = new Set(previous.split('|'))
  let newest: string | null = null
  for (const entry of next.split('|')) {
    if (entry.length === 0 || seen.has(entry)) {
      continue
    }
    newest = entry.slice(0, entry.lastIndexOf(':'))
  }
  return newest && newest.length > 0 ? newest : null
}

/**
 * A persisted message as this page holds it.
 *
 * The partition lives HERE as well as on the stream path, and that is the whole
 * point: `KnowledgeThreadMessage.citations` is the mixed
 * `KnowledgeAskCitation | AssistantCitation` array the server writes into one
 * jsonb column, so a thread that renders correctly during a session would break
 * on the next reload if reload were the one reader that skipped the split.
 *
 * Empty halves stay `undefined` rather than becoming `[]`, so a legacy grounded
 * message reloads byte-identically to how it did before web citations existed.
 */
function messageToTurn(message: KnowledgeThreadMessage): LocalTurn {
  const { documentCitations, webCitations } = partitionAssistantCitations(
    message.citations,
  )

  return {
    id: message.token,
    role: message.role === 'assistant' ? 'assistant' : 'user',
    text: message.text,
    askId: message.askId,
    createdAt: message.createdAt,
    refused: message.refused,
    gaps: message.gaps ?? undefined,
    citations: documentCitations.length > 0 ? documentCitations : undefined,
    webCitations: webCitations.length > 0 ? webCitations : undefined,
    // Agent C: rehydrate plan from the proposing message when cardEligible.
    plan:
      message.plan && message.plan.cardEligible && message.plan.steps.length >= 2
        ? message.plan
        : undefined,
    charts: asAnalystCharts(message.charts),
    tables: asAnalystTables(message.tables),
    creativeDrafts:
      message.creativeDrafts && message.creativeDrafts.length > 0
        ? message.creativeDrafts
        : undefined,
    socialTrackerProposal: message.socialTrackerProposal ?? undefined,
  }
}

function turnMessageToken(turn: { id: string; serverToken?: string }): string {
  return turn.serverToken ?? turn.id
}

function stampCreativeDraftsFromConfirm(args: {
  drafts: Array<AssistantCreativeDraft>
  artifacts: Array<ThreadArtifactDto>
  creativeDrafts?: Array<AssistantCreativeDraft>
  confirmedAt: string
}): Array<AssistantCreativeDraft> {
  const { drafts, artifacts, creativeDrafts, confirmedAt } = args
  if (creativeDrafts && creativeDrafts.length > 0) {
    return creativeDrafts
  }
  const remaining = artifacts.slice()
  return drafts.map((draft) => {
    const matchIndex = remaining.findIndex((artifact) => {
      if (draft.publicId && artifact.publicId === draft.publicId) {
        return true
      }
      if (artifact.body && artifact.body === draft.body) {
        return true
      }
      if (
        artifact.title === draft.title ||
        (draft.title.length > 0 && artifact.title.includes(draft.title))
      ) {
        return true
      }
      return false
    })
    if (matchIndex < 0) {
      return {
        ...draft,
        confirmedAt,
        publicId: draft.publicId ?? null,
      }
    }
    const match = remaining.splice(matchIndex, 1)[0]
    return {
      ...draft,
      confirmedAt,
      publicId: match?.publicId ?? draft.publicId ?? null,
    }
  })
}

function isConflictStatus(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  return (error as { response?: { status?: number } }).response?.status === 409
}

function confirmErrorMessage(error: unknown): string {
  const fromResponse = (
    error as { response?: { data?: { message?: string } } }
  )?.response?.data?.message
  if (typeof fromResponse === 'string' && fromResponse.trim()) {
    return fromResponse
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'That save failed'
}

export default function ThreadDetailPage() {
  const { t } = useTranslation('app')
  const { user: authUser } = useAuth()
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const locationState = (location.state || {}) as ThreadLocationState
  /* Frontend review BLOCKER 1: same hook, same shape as every other new Assistant
     Platform v1 surface, so the thread composer's effort toggle and workflow door
     agree with the server-enforced flag rather than rendering unconditionally. */
  const assistantPlatformEnabled = useAssistantPlatformEnabled()

  // --- Agent C + D seam: plan proposal card lifecycle (D owns execution) ---
  const {
    plansByMessageId,
    registerProposal,
    confirmSoundsGood,
    dismiss: dismissPlan,
    skipCurrent,
    retryCurrent,
    markCurrentRunning,
    markCurrentDone,
    markCurrentFailed,
    refreshAfterTurn,
  } = useThreadPlanExecution(token)
  /**
   * Which proposing message's workflow Start last opened ThreadCommandSeed.
   * Used to mark that step running/done/failed when the confirm path arms or
   * starts a run. Cleared when the run reaches a terminal status.
   */
  const planRunMessageIdRef = useRef<string | null>(null)
  /**
   * A prepared run's reader message, held for `handleArmCommand` (D3, Step 4
   * of the twin-test-question-shape plan). The seed itself still carries
   * `preparedSeed.rest` unchanged: the template's shape (whether the first
   * text input is the composer-owned optional ask, `hideFirstOptionalTextOnCard`)
   * is only known once the confirm card has fetched it, which is exactly
   * when `handleArmCommand` receives the full `template`. Set only for a
   * real ask (`planMessageId` null); cleared for the approved-plan "go
   * ahead" path so that mirror keeps reading `rest` as it always has.
   */
  const pendingPreparedReaderAskRef = useRef<{
    slug: string
    message: string
    rest: string
  } | null>(null)
  const askingRef = useRef(false)
  const pendingCreativeAutosavesRef = useRef(
    new Map<string, Map<string, Promise<void>>>(),
  )
  const stoppedCreativeAutosaveRef = useRef(new Set<string>())
  const [confirmingDraftMessageToken, setConfirmingDraftMessageToken] =
    useState<string | null>(null)
  const [savedDraftMessageTokens, setSavedDraftMessageTokens] = useState<
    Record<string, boolean>
  >({})
  // --- end Agent C + D seam ---

  const [thread, setThread] = useState<KnowledgeThreadView | null>(null)

  /* The thread's own prose, in the language its answers are written in (D3 and D4). The
     language is carried BY THE THREAD, chosen once when it was created and persisted, so
     nothing here holds language state of its own and a follow-up can never drift out of
     the language the thread has been answering in. Null falls back to English. */
  const threadCopy = getThreadPlaceholders(thread?.language)
  /* A thread minted by a workflow is readable by the whole organization but
     writable only by its owner (Step 1 / D0), so this page can be looking at
     somebody else's thread. Explicit `=== false`: while the thread record is
     still loading, and on a response written before the flag existed, the
     composer stays as it was rather than flashing a read-only notice at the
     owner of their own thread. */
  const isReadOnlyForViewer = thread?.isOwner === false
  const [turns, setTurns] = useState<Array<LocalTurn>>([])
  const socialTrackers = useSocialTrackers(true)
  const hasSocialTrackers = socialTrackers.isEnabled && socialTrackers.trackers.length > 0
  const [loading, setLoading] = useState(true)
  const [asking, setAsking] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [documents, setDocuments] = useState<Array<KnowledgeDocumentListItem>>(
    [],
  )
  const [scope, setScope] = useState<KnowledgeScopeSelection>({
    web: true,
    library: true,
    mode: 'all',
    documentTokens: [],
    collectionTokens: [],
  })
  const [pickerOpen, setPickerOpen] = useState(false)
  const [railOpen, setRailOpen] = useState<boolean>(readPersistedRailOpen)
  const [railMounted, setRailMounted] = useState<boolean>(railOpen)
  // Mounted vs. open-on-screen. A rail restored from storage is both on the
  // first paint — there is nothing to animate into on a page that is still
  // loading — so this starts where `railOpen` does.
  const [railSlidIn, setRailSlidIn] = useState<boolean>(railOpen)
  const [sourceTabs, setSourceTabs] = useState<Array<ThreadSourceTab>>([])
  const [activeSourceKey, setActiveSourceKey] = useState<string | null>(null)
  /* Which artifact, if any, has taken over the drawer in place of the Outputs
     list — set by an `ArtifactPreviewCard`'s expand affordance, wherever it is
     clicked (the inline stack under the conversation or the rail itself). Key
     rather than the item: a run refresh can reflow `runArtifacts`, and a
     stale key should fall back to the list rather than pin stale content, the
     same way `sourceTabs` never freezes citation data. */
  const [expandedArtifactKey, setExpandedArtifactKey] = useState<
    string | null
  >(null)
  /* Full-viewport Expand (ChatGPT Maximize). Separate from the rail pane
     takeover so the pane can stay open while the reader goes fullscreen. */
  const [fullscreenArtifactKey, setFullscreenArtifactKey] = useState<
    string | null
  >(null)
  /* Session latch: the user turn that owned the latest run when it first
     appeared. Follow-ups must not steal the study pin if timestamps are
     missing or all equal. */
  const [latchedStudyAsk, setLatchedStudyAsk] = useState<{
    runToken: string
    turnId: string
  } | null>(null)
  /* Per-turn reasoning effort (`EffortToggle` with `appearance="external"`).
     Local to the thread session: it is a knob on the next question, not a
     property of the thread, so it is not persisted and not sent to the server
     until Step 7.1 wires the turn param. */
  const [effort, setEffort] = useState<AssistantEffort>('medium')
  /* What the context ring shows, taken from the LAST completed assistant turn's
     `done` frame and from nowhere else: `contextUsage` is not persisted on the
     message row, so after a reload this is legitimately null until the session's
     first turn finishes and the ring renders nothing at all. Persisting it is a
     schema change and out of scope for v1. `historyTruncated` is named for what
     the wire field `truncated` actually means — history dropped oldest-first to
     fit the ceiling — and says nothing about retrieval, which is truncated
     server-side and never reported. */
  const [contextUsage, setContextUsage] =
    useState<AssistantContextUsage | null>(null)
  const [historyTruncated, setHistoryTruncated] = useState(false)
  const [resultsSegmentBy, setResultsSegmentBy] = useState<string | null>(null)
  /* The composer's workflow entry (decision D3), the thread-side half of the
     same door `HomeLabStage` opens. Slug rather than the whole record, so the
     armed choice survives a template list refresh. Persisted in sessionStorage
     for this thread token so F5 / HMR does not silently disarm mid-intake.
     `armedTemplate` is the record the command menu handed over: `GET /workflows`
     hides every `thread-action-*` slug from the lobby list, so a command armed
     by slug alone would never resolve. Null when `WorkflowMenu` armed a lobby
     workflow, which the slug path resolves. `armedInputs` is what the confirm
     screen answered (`{ audience: <id> }` for `/test`). `intakeDirection` is
     the settled ask from the last intake `direction` tail — set, the page
     auto-starts once and latches so a failed start does not loop. */
  /* Remembered-audience scope: the current organization, not the browser
     tab (Decision 1, thread-audience-remember-and-avatar-fallback plan).
     `useCurrentOrg` resolves asynchronously, so `useArmedWorkflowState`
     re-hydrates `rememberedAudience` in its own effect once `org.id` lands
     and again on every later org switch. */
  const { org: currentOrg } = useCurrentOrg()
  const {
    slug: armedWorkflowSlug,
    template: armedTemplate,
    inputs: armedInputs,
    intakeDirection,
    audienceName: armedAudienceName,
    latchedStartKey,
    wasDropped: wasArmedWorkflowDropped,
    rememberedAudience,
    armSlug,
    clear: clearArmedWorkflow,
    armCommand,
    setIntakeDirection,
    latchIntakeStart,
    dismissDrop: dismissArmedWorkflowDrop,
    rememberAudience,
    forgetRememberedAudience,
    proceedWithoutStimulus: seededProceedWithoutStimulus,
  } = useArmedWorkflowState(token, currentOrg?.id ?? null)
  /* Lazy-init reads the D1 hand-off's seed on the SAME render the hook itself
     hydrates it from sessionStorage — a composer that already resolved "proceed
     without a stimulus" must not ask again once this page starts the armed run.
     Every later reset of this ref (disarm, run start, drop) still happens exactly
     where it always did; this only changes what the ref starts as. */
  const armedProceedWithoutStimulusRef = useRef(seededProceedWithoutStimulus)
  const [referencesStimulus, setReferencesStimulus] = useState(false)
  /** Typed ask held when the armed (non-intake) media gate blocks start. */
  const [blockedArmedAsk, setBlockedArmedAsk] = useState<string | null>(null)
  const stimulusReferenceCacheRef = useRef<{
    key: string
    referencesStimulus: boolean
  } | null>(null)
  /** Latch the audience fetch once this session arms — never on remount. */
  const [audiencesPrelatched, setAudiencesPrelatched] = useState(false)
  /* What a typed command hands to the menu. A nonce rather than a boolean so
     the same command typed twice in a row still opens its confirm screen the
     second time. */
  const [commandSeed, setCommandSeed] = useState<ThreadCommandSeed | null>(null)
  /** Prefill the composer after `/test` arms with a seeded or plan ask. */
  const [composerAskSeed, setComposerAskSeed] = useState<{
    text: string
    nonce: number
  } | null>(null)
  const conversationRef = useRef<HTMLDivElement>(null)
  /* Whether the conversation has been scrolled off its top. The header
     scroll-fade only earns its place once there is content passing behind the
     header to dissolve: at rest on a fresh thread the first question sits at
     the top of the column, and a mask painted over it just washes out text
     nobody scrolled. */
  const [conversationScrolled, setConversationScrolled] = useState(false)
  /* The composer floats over the bottom of the conversation, so the column has
     to reserve exactly as much room as the composer actually takes. Measured
     rather than a fixed padding class: the box grows with the action chips, an
     armed workflow chip, the turn chrome row and the textarea itself, and the
     thing that must never be covered is the last thing in the thread (today the
     docked approval gate on the composer, which is where the money is spent). */
  const [composerHeight, setComposerHeight] = useState(0)
  const composerResizeRef = useRef<ResizeObserver | null>(null)
  /* A callback ref, not an effect: the composer mounts only once the thread has
     loaded, which is AFTER this component's early returns, and an effect placed
     where it could see that node would be a hook after a conditional return. */
  const measureComposerWrap = useCallback((node: HTMLDivElement | null) => {
    composerResizeRef.current?.disconnect()
    composerResizeRef.current = null

    if (!node) {
      setComposerHeight(0)
      return
    }

    const measure = () => setComposerHeight(node.getBoundingClientRect().height)
    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    composerResizeRef.current = observer
  }, [])
  const lastQuestionRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const pendingSentRef = useRef(false)
  const didInitialScrollRef = useRef(false)
  /* When the newest question was last parked, and the scroller width that park
     was measured against. See the resize observer below. */
  const lastParkAtRef = useRef(0)
  /* Stamped when a thread is opened. While it is fresh, content that
     renders in late (living artifacts arrive on their own fetch) re-lands
     the reader at the end instead of leaving them mid-column. */
  const arrivedAtRef = useRef(0)
  const lastConversationWidthRef = useRef<number | null>(null)
  /* True while the docked gate is tweening its own height open or shut. That
     tween moves the composer's border box every frame, which both observers
     below read as layout movement and would answer with a scroll correction
     per frame, chasing a scrollHeight that is still moving: the dock appears
     to overshoot and snap back as the last correction lands. A height we are
     animating deliberately is not content arriving late, so the corrections
     sit the animation out. */
  const dockTweeningRef = useRef(false)
  /* A ref and not state: flipping this must not itself render the page, or
     the render would be one more layout change for the observers to answer. */
  const handleDockTweenChange = useCallback((active: boolean) => {
    dockTweeningRef.current = active
  }, [])
  /* Read from scroll effects and from the resize observer, which is created
     once per `loading` and would otherwise close over a stale `isRunActive`.
     A layout effect declared before those keeps it current for the commit
     they run in. */
  const isRunActiveRef = useRef(false)
  /* Signature of the settled steps this thread has already reacted to, plus
     the step whose group still owes a scroll. See the effect below. */
  const settledScrollRef = useRef<{
    signature: string | null
    stepId: string | null
  }>({ signature: null, stepId: null })
  /**
   * Unanchored-citation results, keyed on the turn object itself. A streaming
   * token replaces only the newest turn, so every older answer keeps its entry
   * and `injectCitationMarkers` is not re-run across the whole thread on each
   * frame. Weak so finished turns are collectable with the turn list.
   */
  const unplacedCacheRef = useRef(new WeakMap<LocalTurn, Array<number>>())
  const [conversationHeight, setConversationHeight] = useState(0)

  const docsByToken = useMemo(() => {
    const map = new Map<string, KnowledgeDocumentListItem>()
    for (const doc of documents) {
      map.set(doc.token, doc)
    }
    return map
  }, [documents])

  /* The ONE way this page reads a run. The hook returns fixtures today and the
     live socket after Step 7.1; nothing below knows which. */
  const {
    run,
    template: runTemplate,
    isLoading: runLoading,
    /* A read or an action the server refused, notably the payment guard, which
       covers the plain thread lookup too. The pane says so rather than showing
       the "no run in this thread" empty state, which would read as "nothing
       here" for an org that has runs it is simply not allowed to read. */
    error: runError,
    submitGate,
    reviseStep,
    retryStep,
    cancelRun,
    adoptRun,
  } = useWorkflowRun(token || null)
  // W6-QS-c: the run (and its revision) a Compare-segments pick asks cut plots for.
  const cutPlotsSource = useMemo(() => quickStudyCutPlotsSourceFor(run), [run])

  /* Living thread library — rail / inline / deep-link source of truth.
     `useWorkflowRun` stays for the run pane only. */
  const {
    artifacts: livingArtifactDtos,
    isLoading: livingArtifactsLoading,
    error: livingArtifactsError,
    refresh: refreshLivingArtifacts,
  } = useThreadLivingArtifacts(token || null)

  /* ONE answer to "can this thread start a workflow right now": the rule the
     server already enforces (one active run per thread, the 409 behind
     `uq_workflow_runs_active_per_thread`) and the one `ThreadCommandMenu`
     already applies to itself. Engages when a run starts, lifts on a terminal
     status. */
  const isRunActive = Boolean(run && !isWorkflowRunTerminal(run.status))

  /* Questions preview stays on the composer. Every other pending gate —
     scan, brief, report — already has its body in the thread, so Continue
     lives there too. One mount owns the revision handle either way.
     Computed here, beside `isRunActive` and off the same two values, because
     `handleComposerSend` reads it: a hoisted function declaration that ran
     before the old declaration site further down would have hit the TDZ. */
  const docksGateOnComposer = pendingGateDocksOnComposer(run, runTemplate)

  /**
   * Keep the sidebar Recent rail in sync with the thread this page is looking
   * at. Create sites also upsert immediately, but the first ask retitles the
   * thread ("Untitled" → the user's sentence) and only this page sees that GET.
   */
  useEffect(() => {
    if (!thread) {
      return
    }
    upsertSidebarThread(thread)
  }, [thread])

  /**
   * Plan workflow step completion: when the run that a plan Start seeded
   * reaches a terminal status, advance the cursor (or mark failed). Spend /
   * gate UX stays on ThreadRunGate — this only moves the plan playlist.
   */
  useEffect(() => {
    const planMessageId = planRunMessageIdRef.current
    if (!planMessageId || !run || !isWorkflowRunTerminal(run.status)) {
      return
    }
    const plan = plansByMessageId[planMessageId]
    if (!plan || plan.lifecycle !== 'active') {
      return
    }
    const current =
      plan.currentIndex != null ? plan.steps[plan.currentIndex] : null
    if (!current || current.status !== 'running') {
      return
    }
    if (run.status === 'completed') {
      /* Advance the cursor, and STOP. The next step is left `current` for the
         reader to start, from the card's Start or by saying "go" to the run
         completion announcement, which now names that step and asks for
         exactly that.

         Applying the handoff here is what opened a command dialog the moment
         a run finished: the reader had not asked for the next step, had not
         read the results yet, and the announcement telling them what came
         next arrived to find the dialog already on screen. A modal is
         something the reader opens, never something the product opens on
         their behalf. */
      markCurrentDone(planMessageId)
      planRunMessageIdRef.current = null
    } else {
      markCurrentFailed(planMessageId)
      planRunMessageIdRef.current = null
    }
  }, [
    run,
    plansByMessageId,
    markCurrentDone,
    markCurrentFailed,
  ])

  /* Workflow entry, decision D3: the same three hooks `HomeLabStage` mounts, so
     the two composers arm a workflow through one code path rather than two. */
  const { templates: workflowTemplates } = useWorkflowTemplates()
  const { template: armedLobbyWorkflow, isError: isArmedLobbyWorkflowError } =
    useWorkflowTemplate(armedWorkflowSlug ?? undefined)
  /* One armed workflow, two doors. The record the command menu handed over
     wins, because a thread-action template is invisible to the lookup above;
     everything downstream reads this and never has to know which door it came
     through. */
  const armedWorkflow = armedTemplate ?? armedLobbyWorkflow
  /* THE PAGE'S OWN READY-AUDIENCE LIST (plan 2026-09-14-twin-test-bound-audience,
     D4). `ThreadCommandMenu` keeps its own latch for the confirm card and is
     untouched; this second, page-level read is what lets the composer name the
     audience a LIVE run is bound to after an F5, when the menu may never open and
     `clearArmedWorkflow` has already dropped the persisted `audienceName`.
     Requested only when a template that declares an `audience` input is actually
     in play — armed, or running — so no other thread pays for the list. */
  const pageAudiencesEnabled =
    (armedWorkflow?.inputs.some((spec) => spec.type === 'audience') ?? false) ||
    (run !== null &&
      run !== undefined &&
      (runTemplate?.inputs.some((spec) => spec.type === 'audience') ?? false))
  const { audiences: pageAudiences } =
    useWorkflowScheduleAudiences(pageAudiencesEnabled)
  /**
   * The audience the ARMED command is bound to: the confirm screen's own answer
   * first, then the one resolver over the page's list and the remembered
   * audience. Null when nothing can name it — the composer then shows the
   * command chip alone rather than a blank second chip (D4).
   */
  const armedAudienceChipName =
    armedAudienceName ||
    (armedWorkflow
      ? audienceNameForInputs(
          armedWorkflow,
          armedInputs,
          pageAudiences,
          rememberedAudience,
        )
      : null)
  /**
   * The same name for a LIVE run, which no longer has an armed snapshot to read:
   * the id the run was started with, resolved against the list, and only then the
   * `quick_study` step's own `audienceName` — which exists only once the panel has
   * been drawn, and is therefore the fallback rather than the source.
   */
  const runAudienceChipName = useMemo(() => {
    if (!run) {
      return null
    }
    const fromInputs = runTemplate
      ? audienceNameForInputs(
          runTemplate,
          run.inputs,
          pageAudiences,
          rememberedAudience,
        )
      : null
    if (fromInputs) {
      return fromInputs
    }
    for (const step of run.steps) {
      const output = step.outputOverride ?? step.output
      if (output && output.kind === 'quick_study' && output.audienceName) {
        return output.audienceName
      }
    }
    return null
  }, [run, runTemplate, pageAudiences, rememberedAudience])
  /* Latest-value refs for the long-lived socket callback in the settled-step
     effect below, the same shape `isRunActiveRef` uses. `run` itself must NOT
     join that effect's deps: the object changes on every `:step` and `:sync`
     event, so depending on it would unsubscribe, resubscribe and re-list the
     thread's messages once per frame of a live run. Declared before the
     effect so the value it reads belongs to the commit it runs in. */
  const runRef = useRef(run)
  const runAudienceChipNameRef = useRef(runAudienceChipName)
  useLayoutEffect(() => {
    runRef.current = run
  }, [run])
  useLayoutEffect(() => {
    runAudienceChipNameRef.current = runAudienceChipName
  }, [runAudienceChipName])
  const hasArmedStimulus =
    armedWorkflow?.inputs.some(
      (spec) =>
        spec.type === 'stimulus' &&
        hasBoundStimulusValue(armedInputs[spec.key]),
    ) ?? false
  const { startRun: startWorkflowRun, isStarting: isStartingRun } =
    useStartWorkflowRun()

  /* The same list the `Commands` menu shows, mounted a second time on purpose:
     the hook is SWR-backed, so both consumers share one request (D6). The
     composer needs it to know what a typed `/name` resolves to. In-thread
     (D5): the full command list, not the off-thread `/test`-only surface. */
  const { commands: threadCommands, isLoading: threadCommandsLoading } = useThreadCommands(
    assistantPlatformEnabled && Boolean(token),
    { surface: 'thread' },
  )

  /**
   * Whether the armed workflow is one whose send goes to INTAKE rather than
   * straight to a run.
   *
   * Read off the command's own `intake` marker, never off the slug: the same
   * template can be armed through `WorkflowMenu`, and a slug test here would
   * let that door quietly bypass the conversation and spend the run budget on
   * the first thing the reader typed. The marker travels with the command, so
   * whichever door armed it, the send lands in the same place.
   */
  const armedCommand = armedWorkflowSlug
    ? (threadCommands.find((entry) => entry.slug === armedWorkflowSlug) ?? null)
    : null
  const isIntakeArmed = Boolean(armedCommand?.intake)
  /**
   * The armed command is `/report` (D12): its send goes to the SCOPING beat of
   * report mode, and the settled brief is written by an assistant turn rather
   * than started as a run.
   *
   * Read off the command's own `intakeKind`, never off the slug, for the same
   * reason `isIntakeArmed` reads `arms`: the marker travels with the command.
   */
  const isReportIntakeArmed = armedCommand?.intakeKind === 'report'
  /**
   * A slug-only arm (e.g. a hand-off that could not carry the template record)
   * resolves through `armedLobbyWorkflow`'s own SWR fetch. When that fetch
   * fails outright — never for the ordinary "not found" case, which is
   * `isError: false` — the pending dispatch below must not strand the
   * message waiting on a `armedWorkflow` that will never resolve; it toasts,
   * disarms, and falls back to the plain ask. `armedTemplate` already being
   * set (the D1 hand-off's usual path) means there is nothing to fetch, so
   * the lobby fetch's own error is irrelevant.
   */
  const armedTemplateError =
    armedWorkflowSlug !== null && !armedTemplate && isArmedLobbyWorkflowError
  /* A report-kind intake settles the moment it has a brief: there is no
     audience input anywhere in report mode, so waiting on one would leave the
     auto-start gate permanently shut. */
  const settledAudience = isReportIntakeArmed
    ? REPORT_INTAKE_SETTLED_AUDIENCE
    : settledAudienceId(armedWorkflow, armedInputs)
  const intakeStartKey =
    token && intakeDirection && settledAudience
      ? buildIntakeStartLatchKey(token, intakeDirection, settledAudience)
      : null
  const canAutoStartIntake = Boolean(
    isIntakeArmed &&
      (armedWorkflow || isReportIntakeArmed) &&
      intakeStartKey &&
      /* A report brief WRITES INTO the conversation, so unlike a twin-test
         start (which creates a run and touches no turn) it has to wait for the
         conversation to be its own:

         - `!loading`: a brief settled before this mount (F5 mid-conversation)
           would otherwise race the initial load, whose `listMessages` ends in
           a `setTurns` that wipes the turn out from under it.
         - `!asking`: the scoping turn sets the direction BEFORE its own
           `finally` clears `asking`, so firing on that render would meet
           `runReportTurn`'s one-turn-at-a-time guard, bail, and leave the
           latch spent with nothing written.

         Both only delay the fire; the latch is still written exactly once,
         when it actually happens. Twin-test timing is left alone. */
      (!isReportIntakeArmed || (!loading && !asking)),
  )
  const isIntakeStartLatched = Boolean(
    intakeStartKey && latchedStartKey === intakeStartKey,
  )
  /* Retry is failure/remount chrome, not a second CTA. Hydrate with a
     persisted latch shows it immediately; a live auto-start waits until
     `confirmIntakeDirection` resolves null. */
  const [showIntakeRetry, setShowIntakeRetry] = useState(() =>
    Boolean(loadArmedWorkflowState(token).state.latchedStartKey),
  )
  /** Keep talking hid the intake start row; stays until a new direction arrives. */
  const [intakeStartUiDismissed, setIntakeStartUiDismissed] = useState(false)
  /**
   * `confirmIntakeDirection` is in flight. The auto-start awaits the stimulus
   * classifier BEFORE `startWorkflowRun` raises `isStartingRun`, so without
   * this the "Keep talking" row flashed for the length of that call: a
   * direction with no run yet looked like a direction waiting on the reader.
   * The row only comes back when the start resolves with something to show
   * (the stimulus gate unmet, or a refused start with its Retry).
   */
  const [isConfirmingIntake, setIsConfirmingIntake] = useState(false)

  /**
   * The open gate's own handle on chat-driven revision, published by
   * `ThreadRunGate` (Step 8, which owns everything behind it).
   *
   * ROUTE BY THE HANDLE AND NOTHING ELSE. Non-null means a typed message is a
   * revision of the questions the gate is holding; null means it is an
   * ordinary question for the assistant. All three conditions the plan names
   * (a pending gate, `viewerOwnsRun`, an editable gate) are already folded
   * into that null by the gate, so this page re-derives none of them and holds
   * no gate knowledge of its own.
   */
  const [gateRevisionHandle, setGateRevisionHandle] =
    useState<GateRevisionHandle | null>(null)

  /**
   * Bumped once per composer send while the gate docks there (D6, Fernando's
   * ask): `ThreadRunGate` collapses to one line on a genuine increment and
   * never re-collapses on a re-render carrying the same number. Read by
   * nothing else on this page — the gate owns what "collapsed" means.
   */
  const [gateCollapseSignal, setGateCollapseSignal] = useState(0)

  /* Stimuli Anywhere Step 1: hosted composer attachments (ChatImage + url/objectKey/fileId).
     Register on host so send can pass registry tokens. Living artifact promote
     waits for send — do not refresh the Artifacts rail here.
     Step 3 seeds the stimulus input from this list via applySeedStimulus — do not
     send these as thread-message body bytes. */
  const [threadAttachments, setThreadAttachments] = useState<Array<ChatImage>>(
    [],
  )
  const [threadPdfText, setThreadPdfText] = useState<string | null>(null)
  const [threadPdfName, setThreadPdfName] = useState<string | null>(null)
  const [threadAssets, setThreadAssets] = useState<Array<ThreadAssetView>>([])
  const threadAttachmentsRef = useRef<Array<ChatImage>>([])
  const registeredByFileIdRef = useRef<Map<string, string>>(new Map())
  const registerFailedByFileIdRef = useRef<Set<string>>(new Set())
  const inFlightRegisterRef = useRef<Map<string, Promise<string | null>>>(
    new Map(),
  )
  const composerScopedTokenRef = useRef(token)
  const didSwitchThread = composerScopedTokenRef.current !== token
  if (didSwitchThread) {
    composerScopedTokenRef.current = token
    registeredByFileIdRef.current = new Map()
    registerFailedByFileIdRef.current = new Set()
    inFlightRegisterRef.current = new Map()
    setThreadAttachments([])
    setThreadPdfText(null)
    setThreadPdfName(null)
    setThreadAssets([])
  }
  const composerTiles = didSwitchThread ? [] : threadAttachments
  threadAttachmentsRef.current = composerTiles
  /** Composer-state only. Never DELETE registry rows. */
  const clearThreadComposerAttachments = useCallback(() => {
    setThreadAttachments([])
    setThreadPdfText(null)
    setThreadPdfName(null)
  }, [])
  const handleThreadPdfChange = useCallback(
    (text: string | null, name: string | null) => {
      setThreadPdfText(text)
      setThreadPdfName(name)
    },
    [],
  )

  const refreshThreadAssets = useCallback(async () => {
    if (!token) {
      return
    }
    try {
      const listed = await threadAssetsApi.list(token)
      setThreadAssets(listed)
    } catch {
      /* Pane/tiles refresh on reload; a failed list must not break the thread. */
    }
  }, [token])

  const registerComposerTile = useCallback(
    async (threadToken: string, tile: ChatImage) => {
      const fileId = tile.fileId
      const kind = hostableKindFromChatImage(tile)
      if (!fileId || !tile.url || !kind) {
        return null
      }
      if (registeredByFileIdRef.current.has(fileId)) {
        return registeredByFileIdRef.current.get(fileId) ?? null
      }
      const existingInFlight = inFlightRegisterRef.current.get(fileId)
      if (existingInFlight) {
        return existingInFlight
      }
      const promise = (async () => {
        for (let attempt = 0; attempt < THREAD_ASSET_REGISTER_ATTEMPTS; attempt += 1) {
          try {
            const result = await threadAssetsApi.register(threadToken, {
              fileToken: fileId,
              kind,
              ...(tile.label ? { label: tile.label } : {}),
              ...(tile.mimeType ? { mimeType: tile.mimeType } : {}),
            })
            if (composerScopedTokenRef.current !== threadToken) {
              return result.token
            }
            registeredByFileIdRef.current.set(fileId, result.token)
            registerFailedByFileIdRef.current.delete(fileId)
            setThreadAttachments((prev) =>
              prev.map((image) =>
                image.fileId === fileId
                  ? { ...image, registerFailed: undefined }
                  : image,
              ),
            )
            await refreshThreadAssets()
            return result.token
          } catch {
            /* bounded retry */
          }
        }
        if (composerScopedTokenRef.current !== threadToken) {
          return null
        }
        registerFailedByFileIdRef.current.add(fileId)
        setThreadAttachments((prev) =>
          prev.map((image) =>
            image.fileId === fileId ? { ...image, registerFailed: true } : image,
          ),
        )
        return null
      })()
      inFlightRegisterRef.current.set(fileId, promise)
      try {
        return await promise
      } finally {
        inFlightRegisterRef.current.delete(fileId)
      }
    },
    [refreshThreadAssets],
  )

  useEffect(() => {
    if (!token || !assistantPlatformEnabled) {
      return
    }
    for (const tile of composerTiles) {
      const fileId = tile.fileId
      if (!fileId || !tile.url || tile.registerFailed) {
        continue
      }
      if (registerFailedByFileIdRef.current.has(fileId)) {
        continue
      }
      if (registeredByFileIdRef.current.has(fileId)) {
        continue
      }
      if (inFlightRegisterRef.current.has(fileId)) {
        continue
      }
      if (!hostableKindFromChatImage(tile)) {
        continue
      }
      void registerComposerTile(token, tile)
    }
  }, [
    assistantPlatformEnabled,
    registerComposerTile,
    composerTiles,
    token,
  ])

  const handleRemoveAsset = useCallback(
    (assetToken: string) => {
      if (!token) {
        return
      }
      void threadAssetsApi
        .remove(token, assetToken)
        .then(() => {
          const fileIds: Array<string> = []
          for (const [fileId, listedToken] of registeredByFileIdRef.current.entries()) {
            if (listedToken === assetToken) {
              fileIds.push(fileId)
              registeredByFileIdRef.current.delete(fileId)
            }
          }
          if (fileIds.length > 0) {
            const removedFileIds = new Set(fileIds)
            setThreadAttachments((prev) =>
              prev.filter(
                (image) => !image.fileId || !removedFileIds.has(image.fileId),
              ),
            )
          }
          void refreshLivingArtifacts()
          return refreshThreadAssets()
        })
        .catch(() => {
          toast.error(t('threads.detail.toast.removeAttachmentFailed'))
          void refreshThreadAssets()
        })
    },
    [refreshLivingArtifacts, refreshThreadAssets, token, t],
  )

  const handleRemoveComposerTile = useCallback(
    (image: ChatImage) => {
      const fileId = image.fileId
      if (!fileId) {
        return
      }
      registerFailedByFileIdRef.current.delete(fileId)
      const inFlight = inFlightRegisterRef.current.get(fileId)
      if (inFlight) {
        void inFlight.then((assetToken) => {
          if (!assetToken) {
            return
          }
          handleRemoveAsset(assetToken)
        })
        return
      }
      const assetToken = registeredByFileIdRef.current.get(fileId)
      if (!assetToken) {
        registeredByFileIdRef.current.delete(fileId)
        return
      }
      handleRemoveAsset(assetToken)
    },
    [handleRemoveAsset],
  )

  async function collectAttachmentTokens(): Promise<Array<string>> {
    const tokens: Array<string> = []
    const seen = new Set<string>()
    for (const tile of threadAttachmentsRef.current) {
      const fileId = tile.fileId
      if (!fileId || tile.registerFailed) {
        continue
      }
      const inFlight = inFlightRegisterRef.current.get(fileId)
      const resolved = inFlight
        ? await inFlight
        : registeredByFileIdRef.current.get(fileId) ?? null
      if (!resolved || seen.has(resolved)) {
        continue
      }
      seen.add(resolved)
      tokens.push(resolved)
    }
    return tokens
  }
  /* Re-seed on thread switch, never wipe. This effect runs after the first
     commit, so writing `false` here threw away the D1 hand-off the lazy init
     at `armedProceedWithoutStimulusRef` had just read: both consumers fire
     strictly later (the hand-off dispatch waits on `loading`, the auto-start
     on `canAutoStartIntake`), so they always saw `false`. Reading the new
     thread's OWN persisted seed keeps the reset on a real thread switch and
     stops the mount wipe. */
  useEffect(() => {
    armedProceedWithoutStimulusRef.current =
      loadArmedWorkflowState(token).state.proceedWithoutStimulus === true
    setBlockedArmedAsk(null)
  }, [token])
  const { ingestFiles: ingestThreadFiles, pendingCount: threadFilePendingCount } =
    useChatFileIngest({
      images: composerTiles,
      onImagesChange: setThreadAttachments,
      onImagesMerge: setThreadAttachments,
      onPdfChange: handleThreadPdfChange,
      maxImages: 3,
      uploadHosted: uploadHostedChatStimulus,
      acceptVideo: true,
    })
  const stimulusStillHosting = hasStimulusStillHosting(composerTiles)
  const threadSendBlockedHint =
    threadFilePendingCount > 0 || stimulusStillHosting
      ? stimulusStillHosting &&
        composerTiles.some((im) => isVideoChatImage(im) && !im.url)
        ? t('threads.detail.uploadingVideo')
        : t('threads.attach.uploading')
      : undefined

  /* D7: an armed workflow owns the textarea — its placeholder IS the workflow's
     text input label — so a reply that happens to start with `/` must reach the
     workflow, not the command parser. Withholding both props turns every typed
     command path off inside `ChatComposer` until the workflow is cleared. */
  const composerCommands = armedWorkflowSlug
    ? undefined
    : threadCommands.map((entry) => ({
        command: entry.command,
        label: entry.label,
        description: entry.prompt,
        token: entry.token,
      }))

  /* Returns false for a name nothing matches, which is what makes the composer
     keep the text and say "No such command" rather than sending it as prose
     (D5). A command typed before the list resolves lands here too, and is
     honestly unknown: the text survives, so a second Enter works. */
  function handleTypedCommand(command: string, rest: string): boolean {
    /* Case-insensitive, to match how the dropdown filters: `/Report` offers the
       report row, so `/Report` + Enter has to run it rather than report itself
       as unknown. */
    const typed = command.toLowerCase()
    const match = threadCommands.find((entry) => matchesTypedCommand(entry, typed))
    if (!match) {
      return false
    }

    /* Opens the confirm screen and nothing more. The typed path and the picked
       path converge on the menu's own gate, which is the only place a run can
       start (D4). */
    setCommandSeed((current) => ({
      command: match.command,
      rest,
      nonce: (current?.nonce ?? 0) + 1,
    }))
    return true
  }

  // One piece of state drives both the docked rail and the narrow-viewport
  // overlay: `ThreadReferencesSidebar` decides which it is from its own media
  // query, so the page only has to say "open".
  function changeRailOpen(next: boolean) {
    setRailOpen(next)
    persistRailOpen(next)
  }

  // Opening mounts the drawer closed, then opens it on the next frame: an
  // element that mounts already in its open position has no start frame to
  // transition from, so it would snap. Closing keeps the content mounted for
  // the length of the transition so `overflow-hidden` clips it on the way out.
  // Unmounting on frame one is what made the close animate a blank gap.
  //
  // `railSlidIn` — not `railOpen` — drives both the wrapper width and the
  // drawer transform, so the clip and the slide start on the same frame and
  // stay in lockstep. Driving them from different frames leaves a sliver of
  // page background running down the open edge for the length of the animation.
  useEffect(() => {
    if (railOpen) {
      setRailMounted(true)
      const frame = window.requestAnimationFrame(() => setRailSlidIn(true))
      return () => {
        window.cancelAnimationFrame(frame)
      }
    }
    setRailSlidIn(false)
    const timer = window.setTimeout(() => {
      setRailMounted(false)
    }, RAIL_TRANSITION_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [railOpen])

  // A run does NOT fly the rail open on its own. The gate that needs a
  // decision already renders inline in the thread (`ThreadRunGate`, right
  // where the reader already is), and the header toggle is an expand icon
  // whether or not this drawer is open. The pane is a convenience view of
  // the same steps, not the place the work happens, so starting a run must
  // not cover the conversation the reader is in the middle of. The rail
  // stays exactly as the reader left it; only the explicit header toggle
  // (`changeRailOpen`) moves it.

  function citationLabel(citation: KnowledgeAskCitation): string {
    const lookupKeys = [citation.documentToken, citation.documentName].filter(
      (value): value is string => Boolean(value && value.trim()),
    )
    for (const key of lookupKeys) {
      const doc = docsByToken.get(key)
      if (doc) {
        return documentTitle(doc)
      }
    }
    const name = (citation.documentName || '').trim()
    if (name && !isCitationUuid(name) && name.toLowerCase() !== 'source') {
      return name
    }
    return t('library.facet.source')
  }

  // Tabs are per document, not per citation: following several citations into
  // the same file should land in one tab rather than pile up duplicates.
  function sourceTabKey(citation: KnowledgeAskCitation): string {
    return citation.documentToken || citation.documentName || 'source'
  }

  function openCitation(index: number, citation: KnowledgeAskCitation) {
    const key = sourceTabKey(citation)
    // Following a citation means the reader wants evidence: closing every tab
    // afterwards should return them to the source cards, not to nothing.
    // Session intent, not a preference: this deliberately does not write the
    // stored rail preference, so one chip click never reopens the rail on every
    // future thread. Only the explicit header toggle persists.
    setRailOpen(true)
    setSourceTabs((prev) => {
      if (!prev.some((tab) => tab.key === key)) {
        return [
          ...prev,
          {
            key,
            label: citationLabel(citation),
            citationIndex: index,
            citation,
          },
        ]
      }
      // Keep the tab (and its resolved label), retarget it at this citation.
      return prev.map((tab) =>
        tab.key === key ? { ...tab, citationIndex: index, citation } : tab,
      )
    })
    setActiveSourceKey(key)
  }

  function relabelSourceTab(key: string, label: string) {
    setSourceTabs((prev) =>
      prev.map((tab) =>
        tab.key === key && tab.label !== label ? { ...tab, label } : tab,
      ),
    )
  }

  const groundedTurn = useMemo(() => referencesTurn(turns), [turns])
  const referenceGroups = useMemo(
    () => groupCitationsByDocument(groundedTurn?.citations ?? []),
    [groundedTurn],
  )

  /* The drawer holds stacked Outputs / Workflows / Context / Progress, or an
     opened source that takes over the same shell. Mount when any of those have
     something to show — never auto-open. */
  const hasRun = run !== null
  /* A refused or failed run READ is not the same as a thread with no run:
     `getForThread` returns null for the ordinary "nothing started here" case, so
     an error means the run could not be read at all (the payment guard covers
     the reads too). The pane is the only place that can say so, which is why it
     counts as pane content even with no run in hand. It deliberately does NOT
     feed any auto-open: an org that is refused every read should not have this
     drawer fly open on every thread it visits. */
  const hasRunPane = hasRun || runError !== null
  const runArtifacts = useMemo(() => {
    const foldTokens = foldAssetTokensFromCreativeDtos(livingArtifactDtos)
    const mapped = collectThreadArtifacts(livingArtifactDtos)
    const enriched = enrichStimulusMedia(mapped, threadAssets)
    /* Fold AFTER enrichment so blind shift() leftover pairing is unchanged. */
    return filterFoldedStimulusItems(
      enriched,
      foldTokens,
      livingArtifactDtos,
    )
  }, [livingArtifactDtos, threadAssets])
  /* Study pin shows only this run's living artifacts — not the whole thread library. */
  const studyPinArtifacts = useMemo(() => {
    if (!run?.token) {
      return []
    }
    return runArtifacts.filter((item) => item.runToken === run.token)
  }, [runArtifacts, run?.token])
  const studyDocParts = useMemo(
    () => composeRunStudyDoc(run, runTemplate),
    [run, runTemplate],
  )
  const studyDocPlots = studyDocParts?.plots
  const studyDocQuotes = studyDocParts?.quotes
  const studyDocPopulationId = studyDocParts?.populationId
  const studyDocStrips = studyDocParts?.strips
  const hasArtifacts = runArtifacts.length > 0
  const pendingCreativeDraftCount = useMemo(() => {
    let pending = 0
    for (const turn of turns) {
      pending += countPendingCreativeDrafts(turn.creativeDrafts)
    }
    return pending
  }, [turns])
  const creativeDraftPreviewItems = useMemo(() => {
    const items: Array<ThreadArtifactItem> = []
    for (const turn of turns) {
      const drafts = turn.creativeDrafts
      if (!drafts || drafts.length === 0) {
        continue
      }
      drafts.forEach((draft, index) => {
        items.push(toPreviewItem(draft, draft.body, index))
      })
    }
    return items
  }, [turns])
  const hasThreadAssets = threadAssets.length > 0
  /* Resolved by key every render rather than held as the item itself: a key
     that no longer matches anything in `runArtifacts` (a living-store refresh
     that dropped or reordered artifacts) reads as "nothing expanded" and the
     drawer falls back to the Outputs list instead of showing stale content. */
  const expandedArtifact = useMemo(
    () =>
      expandedArtifactKey
        ? (findExpandableThreadArtifact(
            runArtifacts,
            creativeDraftPreviewItems,
            expandedArtifactKey,
          ) ?? null)
        : null,
    [expandedArtifactKey, runArtifacts, creativeDraftPreviewItems],
  )
  const fullscreenArtifact = useMemo(
    () =>
      fullscreenArtifactKey
        ? (findExpandableThreadArtifact(
            runArtifacts,
            creativeDraftPreviewItems,
            fullscreenArtifactKey,
          ) ?? null)
        : null,
    [fullscreenArtifactKey, runArtifacts, creativeDraftPreviewItems],
  )

  /**
   * StudyDoc lookups for the artifact the reader actually has open, keyed off
   * the ARTIFACT's run rather than the page's.
   *
   * A thread accumulates runs, and the newest one is not the one that wrote
   * the document being read: open a study doc from an earlier run and the
   * plots, quote cards and portrait strip were resolved against a run that
   * knows nothing about its tokens, so the body falls back to `Plot: <id>`
   * placeholders on a document whose markdown is completely intact. A run
   * that is stuck or failed still occupies the newest seat, which is why the
   * fix is to key on the artifact, not to skip unhealthy runs.
   *
   * Lazy: nothing is fetched unless a study doc from another run is open.
   * While that fetch is in flight, or if it fails, the current run's lookups
   * stand, so this can only ever match or improve on the old behavior. Once
   * the run does land, its own parts are used even when they are empty: a
   * document with no plots is honest, a document showing another study's
   * plots is not.
   */
  /* The study doc among whatever is open, fullscreen first. Taking simply the
     topmost artifact would drop these lookups to null whenever a non-doc
     artifact was opened over an expanded doc, quietly regressing the doc
     underneath to the current run's plots. */
  const focusedArtifact =
    (fullscreenArtifact?.slug === STUDY_DOC_SLUG ? fullscreenArtifact : null) ??
    (expandedArtifact?.slug === STUDY_DOC_SLUG ? expandedArtifact : null)
  /* Same SWR key as ThreadRunResults/ThreadInlineArtifacts for this run, so
     this costs no extra fetch — it only needs read access to the map. */
  const { tipForItem: focusedArtifactTipForItem } = useThreadArtifactTips(
    focusedArtifact?.runToken,
  )
  const orgName = (authUser?.organizationName ?? authUser?.companyName)?.trim() || null
  const reportHeadline = focusedArtifact
    ? artifactDisplayLabel({
        ...focusedArtifact,
        body: reconcileArtifactBody(
          focusedArtifact.body,
          focusedArtifactTipForItem(focusedArtifact),
        ),
      }).headline.trim() || null
    : null
  const reportPageTitle = reportHeadline
    ? orgName
      ? `${reportHeadline} · ${orgName}`
      : reportHeadline
    : null
  const focusedRunToken = focusedArtifact?.runToken ?? null
  const foreignRunToken =
    focusedRunToken && focusedRunToken !== run?.token ? focusedRunToken : null
  const { run: focusedRun } = useRunResults(foreignRunToken)
  const { template: focusedTemplate } = useRunTemplate(
    focusedRun?.templateToken ?? null,
  )
  const focusedStudyDocParts = useMemo(() => {
    if (!foreignRunToken) {
      return studyDocParts
    }
    if (!focusedRun) {
      /* Nothing, rather than the current run's. While the fetch is in flight,
         or once it has failed for good, the honest answer is that this
         document's lookups are not available: plots and quotes are keyed by
         uuid and would simply miss, but `populationId` and
         `decisionConfidence` are not, and handing another study's values to
         this document is the exact defect being fixed. Placeholders say "not
         loaded"; the wrong population says nothing at all. */
      return undefined
    }
    return composeRunStudyDoc(focusedRun, focusedTemplate)
  }, [foreignRunToken, focusedRun, focusedTemplate, studyDocParts])
  const focusedPlots = focusedStudyDocParts?.plots
  const focusedQuotes = focusedStudyDocParts?.quotes
  const focusedPopulationId = focusedStudyDocParts?.populationId
  const focusedStrips = focusedStudyDocParts?.strips
  const focusedDecisionConfidence = focusedStudyDocParts?.decisionConfidence

  /**
   * Exhibit lookups for an open REPORT, keyed by `question_id` across the
   * thread rather than against one run (Step 8, D11).
   *
   * A report is written by an assistant turn, not by a run. It has no
   * `sourceRunToken` of its own to key on, and it synthesises across every
   * twin test in the thread, so `{{plot:q1}}` in its body addresses the
   * THREAD's findings and the per-artifact keying above has nothing to
   * answer with.
   *
   * Widening the source widens the id space onto a key that was never
   * thread-unique: `question_id` is `q1`, `q2`, … per run, so two twin tests
   * in one thread collide on it by default. `composeThreadExhibits` retires
   * every id more than one finding answers to instead of picking one, because
   * nothing in a report body says which run a sentence came from. The
   * placeholder that leaves behind is the same honesty the study-doc path
   * above protects: a document with no plots is honest, a document showing
   * another study's plots is not. A report that DOES carry lineage (the
   * retired `thread-action-report` workflow's own artifacts) hands its run
   * token in as `preferRunToken`, and that run's ids win outright.
   *
   * Lazy: a thread nobody opened a report in fetches nothing.
   */
  const openReport =
    (fullscreenArtifact?.kind === 'report' ? fullscreenArtifact : null) ??
    (expandedArtifact?.kind === 'report' ? expandedArtifact : null)
  const needsThreadExhibits =
    openReport !== null && hasStudyDocEmbeds(openReport.body)
  const threadExhibitRunTokens = useMemo(() => {
    if (!needsThreadExhibits) {
      return []
    }
    /* The page's own run is passed in below as the object it already holds,
       so it is deliberately not in the fetch list. */
    const tokens: Array<string> = []
    const seen = new Set<string>()
    if (run?.token) {
      seen.add(run.token)
    }
    for (const item of runArtifacts) {
      const itemRunToken = item.runToken
      if (!itemRunToken || seen.has(itemRunToken)) {
        continue
      }
      seen.add(itemRunToken)
      tokens.push(itemRunToken)
    }
    return tokens
  }, [needsThreadExhibits, runArtifacts, run?.token])
  const threadExhibitRuns = useThreadExhibitRuns(threadExhibitRunTokens)
  const threadExhibits = useMemo(() => {
    if (!needsThreadExhibits) {
      return undefined
    }
    return composeThreadExhibits([run, ...threadExhibitRuns], {
      preferRunToken: openReport?.runToken ?? null,
    })
  }, [needsThreadExhibits, run, threadExhibitRuns, openReport?.runToken])
  const reportPlots = threadExhibits?.plots
  const reportQuotes = threadExhibits?.quotes
  const reportStrips = threadExhibits?.strips

  /* Opens the drawer straight to one artifact's full body. Reused by the
     rail's own Outputs list and deep links (`?artifact=`), so the pane stays
     the place for in-drawer reading. Card Maximize uses fullscreen instead. */
  function setArtifactSearchKey(key: string | null) {
    setSearchParams(
      (prev) => writeThreadArtifactSearchParam(prev, key),
      { replace: true },
    )
  }

  function expandArtifact(item: ThreadArtifactItem) {
    const searchKey = item.publicId ?? item.key
    setExpandedArtifactKey(searchKey)
    changeRailOpen(true)
    setArtifactSearchKey(searchKey)
  }

  function openArtifactFullscreen(item: ThreadArtifactItem) {
    setFullscreenArtifactKey(item.publicId ?? item.key)
  }

  function collapseExpandedArtifact() {
    setExpandedArtifactKey(null)
    setArtifactSearchKey(null)
  }

  function closeArtifactFullscreen() {
    setFullscreenArtifactKey(null)
  }

  useEffect(() => {
    const key = readThreadArtifactKey(searchParams)
    if (!key) {
      setExpandedArtifactKey((current) => (current === null ? current : null))
      return
    }
    const match = findThreadArtifactBySearchKey(runArtifacts, key)
    if (!match) {
      return
    }
    const resolvedKey = match.publicId ?? match.key
    setExpandedArtifactKey((current) =>
      current === resolvedKey ? current : resolvedKey,
    )
    if (!railOpen) {
      changeRailOpen(true)
    }
  }, [searchParams, runArtifacts, railOpen])

  /* Living cards and outcome lines refresh when a watched step settles
     (`completed` / `failed`) or the run itself finishes. The hook already
     merges `:step` into `run`; this effect does not fetch the run again.
     Do not invent a second report body here — the markdown lives on the
     artifact, and the announcement is already a short assistant turn. */
  const settledStepRefreshKey = useMemo(
    () => settledStepRefreshSignature(run),
    [run],
  )
  useEffect(() => {
    if (!run) {
      return
    }
    const isTerminal = isWorkflowRunTerminal(run.status)
    if (!isTerminal && settledStepRefreshKey.length === 0) {
      return
    }
    void refreshLivingArtifacts()
    if (!token) {
      return
    }
    let cancelled = false
    function applyMessages(messages: Array<KnowledgeThreadMessage>) {
      /* ONLY workflow-run outcome lines, and only when they are not already
         on screen under either of their two names. Appending anything else is
         what made a whole exchange appear twice, which moved
         `lastQuestionIndex` onto the copy, pushed the study pin into the
         historic slot and parked the scroll on the duplicate: the results read
         as vanishing (D0). Both guards live in the merge. */
      setTurns((prev) =>
        withRunCommandChip(
          mergeRunCompletionAnnouncements(prev, messages.map(messageToTurn)),
          runRef.current,
          runAudienceChipNameRef.current,
        ),
      )
    }
    if (run.status !== 'completed') {
      void knowledgeThreadsApi
        .listMessages(token)
        .then((messages) => {
          if (!cancelled) {
            applyMessages(messages)
          }
        })
        .catch(() => {
          /* A missed refresh still lands on the next full thread load. */
        })
      return () => {
        cancelled = true
      }
    }
    /* The announcement is written by a listener on the run's OUTCOME event,
       while this effect fires on the run's STATUS event, and the two race:
       the status reaches the socket first, and the listener still has several
       round trips and an insert to do. Subscribe to thread message events and
       run a stall repair instead of timed polling. */
    const announcementAskId = `${WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX}${run.token}`
    async function refreshMessages() {
      const messages = await knowledgeThreadsApi.listMessages(token)
      if (cancelled) {
        return false
      }
      applyMessages(messages)
      return messages.some((message) => message.askId === announcementAskId)
    }
    void refreshMessages().catch(() => {
      /* A missed refresh still lands on the next full thread load. */
    })
    connectWebSocket()
    subscribeToKnowledgeThread(token)
    const offMessage = onKnowledgeThreadMessageAdded(token, (event) => {
      if (
        event.askId === announcementAskId ||
        event.askId === null ||
        isWorkflowRunAskId(event.askId)
      ) {
        void refreshMessages().catch(() => undefined)
      }
    })
    return () => {
      cancelled = true
      offMessage()
      unsubscribeFromKnowledgeThread(token)
    }
  }, [
    run?.token,
    run?.status,
    settledStepRefreshKey,
    token,
    refreshLivingArtifacts,
  ])

  /* M17: Re-stamp the launch turn's command chip when the run first becomes
     available after a mid-run reload.
     The initial thread-load effect calls withRunCommandChip(turns, run) with
     run = null when useWorkflowRun has not yet resolved; this effect corrects
     it the moment the run token is known, without waiting for a step to
     settle. Depends only on run?.token so status / step changes do not
     trigger an unnecessary re-stamp (the settledStepRefreshKey effect
     already handles those). */
  useEffect(() => {
    if (!run) return
    setTurns((prev) => withRunCommandChip(prev, run, runAudienceChipName))
    /* `runAudienceChipName` is a trigger as well as an input: the ready-audience
       list lands AFTER the run does on an F5, so the launch turn has to be
       re-stamped the moment the id can finally be named (B4). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.token, runAudienceChipName])

  /* Every run the reader is allowed to see, finished ones included. Progress
     is the thread's ONLY step surface now that the template recipe no longer
     sits beside it, so gating this on a live run would leave a completed
     thread with no record of what ran at all. A terminal run renders the same
     rows in their settled states, which is exactly the record. */
  const showRailProgress = run !== null || runError !== null
  const railToggleLabel = railOpen
    ? t('chatChrome.collapseSidebar')
    : t('chatChrome.expandSidebar')
  // Citations that could not be anchored to a sentence are NEVER rendered
  // inline in the prose; the answer that owns them discloses them instead.
  // Per answer, not per page: citation indexes are numbered within one answer,
  // so index 1 on the first answer is a different source from index 1 on the
  // newest. A single page-level set could only ever describe the newest
  // grounded answer and silently dropped every older answer's unanchored
  // citations in a multi-question thread.
  const unplacedByTurnId = useMemo(() => {
    const cache = unplacedCacheRef.current
    const byTurnId = new Map<string, Array<number>>()
    for (const turn of turns) {
      // A refused answer renders no prose and no markers (ThreadAnswerBody
      // returns early), so it has nothing to disclose either.
      if (turn.role !== 'assistant' || turn.refused) {
        continue
      }
      if (!turn.citations || turn.citations.length === 0) {
        continue
      }
      let unplaced = cache.get(turn)
      if (!unplaced) {
        unplaced = injectCitationMarkers(turn.text, turn.citations).unplaced
        cache.set(turn, unplaced)
      }
      if (unplaced.length > 0) {
        byTurnId.set(turn.id, unplaced)
      }
    }
    return byTurnId
  }, [turns])

  /**
   * Where the current exchange starts. The newest question and everything the
   * thread has produced since it — the answer, the run's busy line, its
   * results, its artifacts — are ONE group, and that group is what carries the
   * parking floor below. An index rather than an id because the render has to
   * split the turn list at it.
   */
  const lastQuestionIndex = useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'user') return i
    }
    return -1
  }, [turns])
  const lastQuestionId =
    lastQuestionIndex >= 0 ? turns[lastQuestionIndex].id : null
  const runStartAt = resolveWorkflowRunStartAt(run)
  const resolvedOriginatingAskId = originatingCommandUserTurnId(
    turns,
    runStartAt,
  )

  useEffect(() => {
    if (!run) {
      setLatchedStudyAsk(null)
      return
    }
    if (latchedStudyAsk?.runToken === run.token) {
      return
    }
    const turnId = resolvedOriginatingAskId ?? lastUserTurnId(turns)
    if (!turnId) {
      return
    }
    setLatchedStudyAsk({ runToken: run.token, turnId })
  }, [run, resolvedOriginatingAskId, latchedStudyAsk, turns])

  const originatingCommandTurnId =
    run && latchedStudyAsk?.runToken === run.token
      ? latchedStudyAsk.turnId
      : resolvedOriginatingAskId

  /**
   * Room enough under the newest question that it can actually reach the top of
   * the scroller, so a one-line answer parks the same way a long one does.
   *
   * A FLOOR ON THE WHOLE EXCHANGE, never a height on the prose. Carried by the
   * answer alone it stopped being trailing the moment anything rendered after
   * the turn list: the run's busy line, its results and its artifacts are all
   * appended to the reading column, so the floor became a viewport-tall hole
   * wedged between the answer and a run block it had no idea existed. On the
   * group it self-solves — a run reports back, the group grows past the floor,
   * and the padding is simply gone. Nothing here needs to know how tall the run
   * block is, or that there is one.
   *
   * Not always the applied value: a live run whose pin sits above this box
   * suppresses the floor (`appliedExchangeMinHeight`, D6).
   */
  const exchangeMinHeight =
    lastQuestionIndex >= 0 && conversationHeight > 0
      ? Math.max(0, conversationHeight - QUESTION_HEADROOM_PX)
      : 0

  // Park the newest question at the top of the conversation rather than pinning
  // the bottom. A long answer then grows downward into empty space instead of
  // dragging the question off-screen, so the reader keeps what they asked in
  // view while it is being answered. The first park is instant so opening a
  // thread does not animate from the top; later questions still ease in.
  /**
   * Land the reader on the newest thing in the thread.
   *
   * A run's output reads in run order (`groupArtifactsByStep`), so the
   * deliverable is the LAST group, under the working material that produced
   * it. Opening a finished thread at its end is therefore how the reader
   * meets the answer first, and it is what arriving at any conversation
   * normally does.
   */
  function scrollToEnd(behavior: 'smooth' | 'instant') {
    const container = conversationRef.current
    if (!container) return
    const top = container.scrollHeight
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior })
    } else {
      container.scrollTop = top
    }
  }

  function parkLastQuestion(behavior: 'smooth' | 'instant') {
    const container = conversationRef.current
    const question = lastQuestionRef.current
    if (!container || !question) return
    const offset =
      question.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop
    const top = Math.max(0, offset - 8)
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior })
    } else {
      container.scrollTop = top
    }
  }

  /* Declared before every effect that reads the ref, so the value they see
     belongs to the commit they are running in. */
  useLayoutEffect(() => {
    isRunActiveRef.current = isRunActive
  }, [isRunActive])

  useLayoutEffect(() => {
    if (!lastQuestionId) return
    const isArrival = !didInitialScrollRef.current
    didInitialScrollRef.current = true
    /* Stamped here, not inside `parkLastQuestion`: a re-park must not extend
       its own window, or a width that keeps changing keeps re-parking. */
    lastParkAtRef.current = Date.now()
    if (isArrival) {
      /* Arriving: go to the end. Parking the question at the top is for a
         question asked in this session, where the answer has yet to arrive
         and the reader wants to keep what they asked in view. Someone opening
         a thread that already ran wants its outcome, not its opening line. */
      arrivedAtRef.current = Date.now()
      scrollToEnd('instant')
      return
    }
    if (isRunActiveRef.current) {
      /* A message typed while a run is in flight is not a new question
         waiting on an answer: the run's cards are above it and still
         arriving, and parking the message at the top would hide them (D6).
         Go to the end, where the message and the busy line both are. */
      scrollToEnd('smooth')
      return
    }
    parkLastQuestion('smooth')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastQuestionId])

  /* A step settling mid-run replaces or adds its card several screens down.
     Bring that group into view once it is actually rendered: the signature
     changes on the `:step` event, while the card itself lands on the living
     artifacts fetch the effect above starts, so the pending step id is held
     until a group carrying it exists. The first pass on a live run only
     seeds the signature — opening a thread mid-run must not yank the
     reader. */
  useEffect(() => {
    const state = settledScrollRef.current
    if (!isRunActive) {
      state.signature = null
      state.stepId = null
      return
    }
    if (state.signature !== settledStepRefreshKey) {
      const seenSignature = state.signature
      state.stepId =
        seenSignature === null
          ? null
          : newestSettledStepId(seenSignature, settledStepRefreshKey)
      state.signature = settledStepRefreshKey
    }
    if (!state.stepId) return
    const container = conversationRef.current
    const group = container?.querySelector(
      `[data-step-id="${state.stepId}"]`,
    )
    if (!group || typeof group.scrollIntoView !== 'function') return
    state.stepId = null
    group.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [isRunActive, settledStepRefreshKey, studyPinArtifacts.length])

  /* Living artifacts and run steps land on their own fetches, after the
     arrival scroll has already run against a shorter column. Re-land while
     the arrival is still fresh, on the same window the re-park uses.
     H6: include conversationHeight so this fires once the trailing-padding
     floor is measured (initial scrollToEnd runs before that layout effect
     and reads a short scrollHeight; the first non-zero conversationHeight
     triggers this correction).
     M15: skip if the reader has scrolled away from the end — a yank while
     they are reading the thread is hostile. 40 px slop absorbs momentum /
     fractional-pixel rounding without masking a real user scroll. */
  useLayoutEffect(() => {
    if (!arrivedAtRef.current) return
    if (dockTweeningRef.current) return
    if (Date.now() - arrivedAtRef.current >= REPARK_AFTER_PARK_MS) return
    const container = conversationRef.current
    if (container) {
      const distanceFromEnd =
        container.scrollHeight - container.scrollTop - container.clientHeight
      if (distanceFromEnd > 40) return
    }
    scrollToEnd('instant')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length, studyPinArtifacts.length, run?.steps?.length, conversationHeight])

  // The trailing answer is padded to fill the viewport so scrolling the
  // question to the top is actually possible on short answers too. Layout
  // effect so the first paint already has the height — measuring after paint
  // is the bubble growing from small to large.
  useLayoutEffect(() => {
    const container = conversationRef.current
    if (!container) return
    setConversationHeight(container.clientHeight)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setConversationHeight(entry.contentRect.height)
      /* A park measures the question BEFORE the layout it lands in: sending a
         command opens the run pane in the same beat, the column narrows, and
         everything above the question reflows taller, so the scroll lands a
         screen short of it. A width change inside the window after a park
         re-parks against the settled layout; an ordinary window resize a
         minute later leaves the reader's scroll alone. */
      const width = entry.contentRect.width
      const previousWidth = lastConversationWidthRef.current
      lastConversationWidthRef.current = width
      if (
        previousWidth !== null &&
        width !== previousWidth &&
        !dockTweeningRef.current &&
        Date.now() - lastParkAtRef.current < REPARK_AFTER_PARK_MS
      ) {
        /* Correcting the same landing the park effect made, so it has to
           land the same way: a live run went to the end, not to the top. */
        if (isRunActiveRef.current) {
          scrollToEnd('smooth')
        } else {
          parkLastQuestion('smooth')
        }
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  /* Keyed on `loading` for the same reason the height observer above is: the
     scroller mounts only once the thread has loaded. Reads on attach as well as
     on scroll, so a thread restored mid-scroll (the question-parking layout
     effect scrolls programmatically) starts in the right state. */
  useEffect(() => {
    const container = conversationRef.current
    if (!container) return
    const sync = () => {
      setConversationScrolled(container.scrollTop > 4)
    }
    sync()
    container.addEventListener('scroll', sync, { passive: true })
    return () => container.removeEventListener('scroll', sync)
  }, [loading])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    pendingSentRef.current = false
    didInitialScrollRef.current = false
    arrivedAtRef.current = 0
    void (async () => {
      setLoading(true)
      try {
        const [threadView, messages, docs, listedAssets] = await Promise.all([
          knowledgeThreadsApi.get(token),
          knowledgeThreadsApi.listMessages(token),
          knowledgeApi.listDocuments().catch(() => []),
          assistantPlatformEnabled
            ? threadAssetsApi.list(token).catch(() => [])
            : Promise.resolve([] as Array<ThreadAssetView>),
        ])
        if (cancelled) return
        setThread(threadView)
        setTitleDraft(threadView.title)
        setScope(
          locationState.scope ||
            threadView.scope || {
              web: true,
              library: true,
              mode: 'all',
              documentTokens: [],
              collectionTokens: [],
            },
        )
        setTurns(
          withRunCommandChip(
            messages.map((message) => {
              const turn = messageToTurn(message)
              const attachments = turnAttachmentsFromAssets(
                listedAssets,
                message.token,
              )
              return attachments ? { ...turn, attachments } : turn
            }),
            run,
            runAudienceChipName,
          ),
        )
        setThreadAssets(listedAssets)
        const listedFileIds = new Set<string>()
        for (const asset of listedAssets) {
          const fileId = listedAssetFileId(asset)
          if (!fileId) {
            continue
          }
          listedFileIds.add(fileId)
          registeredByFileIdRef.current.set(fileId, asset.token)
        }
        for (const tile of threadAttachmentsRef.current) {
          const fileId = tile.fileId
          if (!fileId || !tile.url) {
            continue
          }
          if (listedFileIds.has(fileId)) {
            registerFailedByFileIdRef.current.delete(fileId)
            continue
          }
          if (
            !registeredByFileIdRef.current.has(fileId) &&
            !inFlightRegisterRef.current.has(fileId) &&
            assistantPlatformEnabled
          ) {
            registerFailedByFileIdRef.current.delete(fileId)
            void registerComposerTile(token, tile)
          }
        }
        // --- Agent C: rehydrate Suggested plan cards from message-attached proposals ---
        for (const message of messages) {
          if (
            message.role !== 'assistant' ||
            !message.plan ||
            !message.plan.cardEligible
          ) {
            continue
          }
          const proposal = planTailToProposal(message.token, message.plan)
          if (proposal) {
            registerProposal(proposal)
          }
        }
        // --- end Agent C ---
        setDocuments(docs)
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? t('threads.detail.toast.threadNotFound')
        toast.error(message)
        navigate('/threads', { replace: true })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // Reload when the assistant flag hydrates so GET-assets is not skipped forever.
  }, [token, assistantPlatformEnabled])

  /**
   * D1's hand-off lands here: an off-thread composer seeds `ArmedWorkflowState`
   * and navigates with `{ pendingMessage, pendingEffort, scope }`, and this is
   * what actually starts the run (or opens intake) — never the composer.
   *
   * Readiness gate: with no armed slug, the ordinary ask is always ready the
   * moment loading clears. With an armed slug, this WAITS for the command
   * table (`isIntakeArmed` reads it) and for the armed template to either
   * resolve or fail — never dispatches on a read that is still in flight,
   * which would misroute an intake-armed command to `runAsk` before its
   * `arms` marker has loaded.
   *
   * Dispatch keys on the COMMAND TABLE, not the template record:
   * `isIntakeArmed` → `runIntakeTurn`; a resolved `armedWorkflow` (and not
   * intake-armed) → `startArmedWorkflow`; a failed template read → toast,
   * disarm, then `runAsk` so the message is never stranded; no arm at all →
   * `runAsk`. `pendingSentRef` latches ONLY once one of those actually fires,
   * so a still-loading command list or template read gets another pass at
   * the next render instead of silently dropping the message.
   *
   * An empty `pendingMessage` with an armed intake still opens intake
   * (`runIntakeTurn('')` is a valid scoping opener); an empty message with a
   * non-intake arm still starts the run with its own `inputs`. With NO arm at
   * all and no message, there is nothing to dispatch.
   */
  useEffect(() => {
    if (loading || !token || pendingSentRef.current) return
    const armedSlug = armedWorkflowSlug
    const isReady =
      armedSlug === null ||
      (!threadCommandsLoading &&
        (isIntakeArmed || armedWorkflow !== null || armedTemplateError))
    if (!isReady) return

    const pending = locationState.pendingMessage?.trim() ?? ''
    if (!pending) {
      /* Nothing handed off from Home Lab's composer. A workflow armed with
         no accompanying message (e.g. picked straight from this page's own
         WorkflowMenu, never routed through the Home Lab hand-off) is not
         this effect's concern — the reader's own composer send is what
         starts it, same as any other armed workflow. Calling
         `startArmedWorkflow('')`/`runIntakeTurn('')` here would silently
         start a run with empty input the instant arming finishes, racing
         ahead of whatever the reader is about to type. Only a broken arm
         still needs cleanup with no pending text to fall back to. */
      if (armedTemplateError) {
        pendingSentRef.current = true
        toast.error(t('workflows.run.couldNotStart'))
        clearArmedWorkflow()
      }
      return
    }

    pendingSentRef.current = true
    /* Frontend review SHOULD FIX 2: honour the effort the home composer already picked,
       carried as `pendingEffort` (mirrors `pendingMessage`, `useHomeLabSubmit.ts`).
       Omitted rather than falsy-checked against a default, so a hand-off that never set
       one (every pre-existing caller) leaves this page's own Medium default untouched. */
    if (locationState.pendingEffort) {
      setEffort(locationState.pendingEffort)
    }
    navigate(location.pathname, { replace: true, state: {} })

    if (isIntakeArmed) {
      void runIntakeTurn(pending)
    } else if (armedWorkflow) {
      void startArmedWorkflow(pending)
    } else if (armedTemplateError) {
      toast.error(t('workflows.run.couldNotStart'))
      clearArmedWorkflow()
      void runAsk(pending)
    } else {
      void runAsk(pending)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    token,
    armedWorkflowSlug,
    threadCommandsLoading,
    isIntakeArmed,
    armedWorkflow,
    armedTemplateError,
  ])

  async function persistTitle() {
    if (!thread) return
    const next = titleDraft.trim()
    if (!next || next === thread.title) return
    try {
      const updated = await knowledgeThreadsApi.update(thread.token, {
        title: next,
      })
      setThread(updated)
      setTitleDraft(updated.title)
    } catch {
      toast.error(t('chatChrome.renameThreadFailed'))
      setTitleDraft(thread.title)
    }
  }

  async function handleWrongPage(citation: KnowledgeAskCitation) {
    if (!citation.citationToken) return
    try {
      await knowledgeApi.reportWrongPage(citation.citationToken)
      toast.success(t('chatChrome.wrongPageLogged'))
    } catch {
      toast.error(t('chatChrome.wrongPageReportFailed'))
    }
  }

  function handleStop() {
    const controller = abortRef.current
    if (!controller || controller.signal.aborted) return
    controller.abort()
  }

  /* An armed workflow never reaches `runAsk`: it is not a question for the
     thread's assistant, it is a run to start. Same split `HomeLabStage` makes,
     through the same hook, so a workflow starts one way on both composers.

     THREE DESTINATIONS NOW:

     1. A gate holding a revision handle takes the message. The handle is
        already the whole test — the gate publishes null the moment a typed
        message is not a revision — so this is one branch, not three.
     2. An armed command that ARMS (today `/test`) sends to intake; any other
        armed workflow starts, unchanged.
     3. Everything else is an ordinary question. */
  function handleComposerSend(text: string) {
    if (gateRevisionHandle && isPlainGateContinue(text)) {
      setTurns((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'user' as const,
          text: text.trim(),
          createdAt: new Date().toISOString(),
        },
      ])
      gateRevisionHandle.approve()
      return
    }
    if (gateRevisionHandle) {
      /* D6: collapse the dock on send, docked placement only — a revisable
         gate rendered inline in the thread has nothing to collapse. */
      if (docksGateOnComposer) {
        setGateCollapseSignal((count) => count + 1)
      }
      void runIntakeTurn(text, gateRevisionHandle)
      return
    }
    /* Intake is tested BEFORE the template, not inside it: `/report` is a
       native command with no template record at all, so gating the intake
       branch on a resolved `armedWorkflow` would drop a report follow-up into
       the ordinary assistant. `/test` always has one, so this reads the same
       for it either way. */
    if (isIntakeArmed) {
      void runIntakeTurn(text)
      return
    }
    if (armedWorkflow) {
      /* LOCKED while a run is live: the server would refuse a second run with
         its one-active-run 409 anyway, so the message falls through to the
         assistant instead of into a refusal. */
      if (!isRunActive) {
        void startArmedWorkflow(text)
        return
      }
    }
    /* D6: a docked gate that is not chat-revisable (not the viewer's run, or
       not editable) publishes no `gateRevisionHandle`, so an ordinary question
       reaches here while it is still on screen. Collapse it the same way. */
    if (docksGateOnComposer) {
      setGateCollapseSignal((count) => count + 1)
    }
    void runAsk(text)
  }

  function latestUserAsk(): string {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'user' && turns[i].text.trim()) {
        return turns[i].text.trim()
      }
    }
    return ''
  }

  /** Plan Start, one-step plan tail, and `done.preparedRun` all share this. */
  function seedPlanWorkflowStart(
    messageId: string,
    slug: string,
    rest = '',
    audience = '',
    audienceHint = '',
    segment?: {
      populationFilter?: ThreadCommandSeed['populationFilter']
      segmentLabel?: string
    },
  ) {
    planRunMessageIdRef.current = messageId
    setCommandSeed((current) =>
      buildPlanWorkflowCommandSeed(
        slug,
        current?.nonce ?? 0,
        rest,
        audience,
        audienceHint,
        segment,
      ),
    )
  }

  function applyPlanStepHandoff(
    messageId: string,
    handoff: ThreadPlanStepHandoff | null,
  ) {
    if (!handoff) {
      return
    }
    if (handoff.kind === 'workflow') {
      const proposing = turns.find((turn) => turn.id === messageId)
      seedPlanWorkflowStart(
        messageId,
        handoff.slug,
        latestUserAsk(),
        handoff.audience,
        proposing?.text ?? '',
      )
      return
    }
    void runAsk(handoff.intent, {
      stepSkillSlug: handoff.slug,
      planMessageId: messageId,
    })
  }

  /**
   * Command menu started a real run (report / slack / lobby sweep). Adopt it
   * and, when the Start came from a plan card, mark that workflow step running.
   */
  function handleCommandRunStarted(
    started: Parameters<typeof adoptRun>[0],
  ) {
    clearThreadComposerAttachments()
    adoptRun(started)
    const planMessageId = planRunMessageIdRef.current
    if (planMessageId) {
      markCurrentRunning(planMessageId)
    }
  }

  /** The workflow menu's door: a slug and nothing else to carry. */
  function handleArmSlug(slug: string) {
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    armSlug(slug)
  }

  /**
   * ARM `/report` and open its scoping beat in one move.
   *
   * Unlike `/test`, a report command collects nothing: there is no audience to
   * pick and no ask the reader has to write first, so the menu's confirm
   * screen would be an empty card. Arming and asking are therefore the same
   * gesture, and the opener is whatever followed the typed name (`/report just
   * the pricing question`) or an empty message, which the scoping prompt reads
   * as "tell me what this thread established".
   *
   * The kind is passed EXPLICITLY rather than read back off `armedCommand`:
   * `armSlug` has not landed in state yet on this tick, and a scoping turn
   * that sent the twin-test prompt would ask about audiences.
   */
  function handleArmReportCommand(command: ThreadCommand, rest: string) {
    handleArmSlug(command.slug)
    setIntakeStartUiDismissed(false)
    void runIntakeTurn(rest, undefined, { kind: 'report' })
  }

  /**
   * ARM the composer from the command menu's confirm screen (`/test`).
   *
   * Both halves are kept: the template, because the lobby lookup cannot see a
   * thread action, and the confirm screen's answers, because the audience the
   * reader picked there is what the run will eventually carry.
   *
   * Confirm never drafts questions. A seeded sentence (`/test how likely…`
   * or a plan `rest`) is dropped into the composer so the reader can edit
   * it; send is what starts intake. An empty arm waits on the placeholder.
   *
   * When Start came from a Suggested plan, mark that workflow step running —
   * twin spend still waits for intake + ThreadRunGate.
   */
  function handleArmCommand(
    template: WorkflowTemplate,
    inputs: Record<string, string | null>,
    armDetails?: { audienceName: string | null },
  ) {
    setAudiencesPrelatched(true)
    /* A fresh arm answers the media gate from scratch: whatever a previous
       arm resolved does not carry into this one. */
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    armCommand(template, inputs, armDetails?.audienceName ?? null)

    const planMessageId = planRunMessageIdRef.current
    if (planMessageId) {
      markCurrentRunning(planMessageId)
    }

    const readerAsk = preparedReaderAsk(
      template,
      inputs,
      pendingPreparedReaderAskRef.current,
    )
    pendingPreparedReaderAskRef.current = null
    const seededAsk = readerAsk ?? inputs[askInputKey(template) ?? '']
    if (typeof seededAsk === 'string' && seededAsk.trim().length > 0) {
      setComposerAskSeed((current) => ({
        text: seededAsk.trim(),
        nonce: (current?.nonce ?? 0) + 1,
      }))
    }
  }

  /**
   * Chip dismiss: drop the arm only. The org-wide remembered audience is NOT
   * erased here (plan 2026-09-14-twin-test-bound-audience, C2/D5): dismissing
   * the armed chip is a per-run undo, not an instruction to forget the
   * audience the org keeps reaching for. Forgetting is now a deliberate act
   * on the confirm card's own checkbox (C3) — see `ThreadCommandMenu`'s
   * `onForgetRememberedAudience`.
   */
  function handleClearArmedWorkflow() {
    armedProceedWithoutStimulusRef.current = false
    setBlockedArmedAsk(null)
    setIntakeStartUiDismissed(false)
    setReferencesStimulus(false)
    stimulusReferenceCacheRef.current = null
    clearArmedWorkflow()
  }

  /**
   * Start the run this conversation settled on.
   *
   * EXPLICIT inputs, never the hook's message mapping: the ask is the sentence
   * intake reported as the direction, verbatim, and the audience is the one
   * the confirm screen picked. Mapping a message here would hand the run
   * whatever happened to be in the textarea instead of the thing both sides
   * agreed to.
   *
   * A refusal — the one-active-run 409 above all, already toasted by the hook —
   * resolves null and leaves the composer ARMED with the direction intact, so
   * the conversation survives and the reader can try again once the other run
   * finishes. Losing it would mean paying for the whole intake a second time.
   */
  async function isStimulusGateSatisfiedAtRunStart(params: {
    template: WorkflowTemplate
    inputs: Record<string, string | null>
    ask: string
    proceedWithoutStimulus: boolean
  }): Promise<boolean> {
    const hasAttachedStimulus = params.template.inputs.some(
      (spec) =>
        spec.type === 'stimulus' &&
        hasBoundStimulusValue(params.inputs[spec.key]),
    )
    const cacheKey = `${hasAttachedStimulus}\u0000${params.ask.trim()}`
    let resolvedReferencesStimulus = false

    if (stimulusReferenceCacheRef.current?.key === cacheKey) {
      resolvedReferencesStimulus =
        stimulusReferenceCacheRef.current.referencesStimulus
    } else {
      try {
        const result = await knowledgeApi.classifyStimulusReference({
          ask: params.ask,
          hasAttachedStimulus,
        })
        resolvedReferencesStimulus = result.referencesStimulus === true
      } catch {
        /* The gate fails open. A classifier outage cannot prevent a run. */
        resolvedReferencesStimulus = false
      }
      stimulusReferenceCacheRef.current = {
        key: cacheKey,
        referencesStimulus: resolvedReferencesStimulus,
      }
    }

    setReferencesStimulus(resolvedReferencesStimulus)
    return hasStimulusGateSatisfied(
      params.template.inputs,
      params.inputs,
      params.proceedWithoutStimulus,
      resolvedReferencesStimulus,
    )
  }

  async function confirmIntakeDirection() {
    if (!token || !intakeDirection || !settledAudience) {
      return
    }
    setIsConfirmingIntake(true)
    try {
      await confirmIntakeDirectionInner()
    } finally {
      setIsConfirmingIntake(false)
    }
  }

  async function confirmIntakeDirectionInner() {
    if (!token || !intakeDirection || !settledAudience) {
      return
    }

    /* D12: a settled REPORT brief is written, never run. No `workflowRunsApi`,
       no template, no spend gate — the brief becomes the message of one
       report-mode assistant turn. Disarm first so the "Keep talking" row and
       the armed chip come down as the writing starts; the turn owns the thread
       from here. */
    if (isReportIntakeArmed) {
      /* Read off the armed state BEFORE the disarm below wipes it. Everything
         the settled scope carries is collected here, so a second field on the
         tail is one more line at this seam and nothing else. */
      const scope: SettledReportScope = { brief: intakeDirection }
      setShowIntakeRetry(false)
      clearArmedWorkflow()
      await runReportTurn(scope)
      return
    }

    if (!armedWorkflow) {
      return
    }

    const askKey = askInputKey(armedWorkflow)
    const runInputs = {
      ...armedInputs,
      ...(askKey ? { [askKey]: intakeDirection } : {}),
    }
    if (
      !(await isStimulusGateSatisfiedAtRunStart({
        template: armedWorkflow,
        inputs: runInputs,
        ask: intakeDirection,
        proceedWithoutStimulus: armedProceedWithoutStimulusRef.current,
      }))
    ) {
      return
    }
    const startedRun = await startWorkflowRun({
      template: armedWorkflow,
      threadToken: token,
      inputs: runInputs,
      /* Intake is the other door to the same place: a command armed from the
         menu (`/test`) is as much a thread action as the armed start below
         tags itself, so the provenance on the run matches whichever door the
         reader came through. */
      ...(armedCommand ? { trigger: 'thread_action' as const } : {}),
      ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflow.slug),
    })
    if (startedRun) {
      setShowIntakeRetry(false)
      armedProceedWithoutStimulusRef.current = false
      clearThreadComposerAttachments()
      adoptRun(startedRun)
      clearArmedWorkflow()
    } else {
      setShowIntakeRetry(true)
    }
  }

  /**
   * THE REPORT TURN: the settled brief, written up.
   *
   * The same endpoint every other turn on this page uses, in `mode: 'report'`
   * (`docs/plans/2026-09-03-assistant-report-mode.md`, D1). Three things make
   * it its own function rather than a flag on `runAsk`:
   *
   * 1. The settled brief IS the user turn, exactly as `/test` renders its own
   *    settled direction. Nest stores it as a `role: 'user'` row on this same
   *    request (`knowledge.service.ts`, the `if (trimmed)` insert), so a page
   *    that skipped it read one bubble short until the next reload — the live
   *    column and the reloaded one disagreed about what the reader agreed to.
   * 2. No `delta` frames arrive (D3). The draft is buffered so the grounding
   *    gate sees all of it, and what streams meanwhile is stage frames in the
   *    ordinary `tool_start` / `tool_end` shape, which the activity line
   *    already renders. An empty `streamed` is therefore normal here.
   * 3. `done.text` is a NOTICE, not the report. The report is `done.report`,
   *    which Nest has already persisted as a living artifact by the time this
   *    frame lands, so the only thing left to do is refetch the library.
   *
   * A refusal (D13's empty thread, 400) is shown with the server's own words:
   * the sentence IS the product copy for that case.
   */
  /**
   * The ONE busy guard the three streaming loops share (`runReportTurn`,
   * `runIntakeTurn`, `runAsk`). The ref is the authority; `asking` is
   * render-only.
   *
   * Two sends in the same tick both read the same stale `asking === false`,
   * both insert their turns, and the second `abortRef.current?.abort()` kills
   * the first stream — which then finishes as "Stopped" for no reason a
   * reader can see. Claiming a ref leaves no gap between the check and the
   * claim, so the second send is simply refused.
   *
   * `endTurn()` in `finally`, always. The intake→ask hand-off then works by
   * construction rather than by `runAsk`'s closure having captured `asking`
   * from an earlier render.
   */
  function beginTurn(): boolean {
    if (askingRef.current) {
      return false
    }
    askingRef.current = true
    setAsking(true)
    return true
  }

  function endTurn() {
    askingRef.current = false
    setAsking(false)
  }

  async function runReportTurn(scope: SettledReportScope) {
    const trimmed = scope.brief.trim()
    if (!token || !trimmed || !beginTurn()) {
      return
    }

    const userTurnId = newId()
    const assistantId = newId()
    setTurns((prev) => [
      ...prev,
      /* Same shape `runIntakeTurn` gives its own user half, and deliberately
         no `commandDisplayName`: `/report` is native, so there is no template
         and no run for `userTurnCommandChipLabel` to reconstruct a chip from
         on reload. Stamping one live would put a chip on screen that a
         refresh takes away again. */
      {
        id: userTurnId,
        role: 'user' as const,
        text: trimmed,
        createdAt: new Date().toISOString(),
      },
      { id: assistantId, role: 'assistant' as const, text: '', streaming: true },
    ])

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null

      for await (const frame of assistantTurnStream(token, trimmed, {
        signal: controller.signal,
        mode: 'report',
      })) {
        if (frame.type === 'delta') {
          /* Not expected under D3, and still handled: a build that starts
             streaming the draft must not render as an empty bubble. */
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'tool_start') {
          const started = frame.tool
          const query =
            typeof frame.query === 'string' ? frame.query.trim() : ''
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: [
                      ...(t.activityLegs ?? []),
                      { tool: started, done: false, ...(query ? { query } : {}) },
                    ],
                  }
                : t,
            ),
          )
        } else if (frame.type === 'tool_end') {
          const ended = frame.tool
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: (t.activityLegs ?? []).map((leg) =>
                      leg.tool === ended ? { ...leg, done: true } : leg,
                    ),
                  }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          throw new Error(frame.message)
        }
      }

      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const notice = done.text.trim() || streamed.trim()
      const assistantMessageToken = done.assistantMessageToken
      /* Same reconciliation the intake and the main sender do: record the
         server's name for each half. The brief's row is the one Nest inserted
         from this request's own `message`, so the local turn and the persisted
         one are two names for one thing rather than two bubbles. */
      const userMessageToken = done.userMessageToken
      /* `turn`, not `t`: the i18n `t` is in scope here and the map parameter
         everywhere else on this page is named for the shadowing it would
         otherwise cause. */
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === userTurnId && userMessageToken
            ? { ...turn, serverToken: userMessageToken }
            : turn.id === assistantId
            ? {
                ...turn,
                text: notice || t('threads.detail.command.report.ready'),
                streaming: false,
                refused: done.refused,
                ...(assistantMessageToken
                  ? { serverToken: assistantMessageToken }
                  : {}),
              }
            : turn,
        ),
      )

      /* The card comes from the library, not from this frame: Nest wrote the
         artifact before it sent `done`, so one refetch is what puts it on
         screen without a reload. No tail means nothing was written and there
         is nothing to refetch. */
      if (done.report?.markdown) {
        await refreshLivingArtifacts()
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      setThread(refreshed)
      setTitleDraft(refreshed.title)
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId
              ? {
                  ...t,
                  text: t.text.trim()
                    ? t.text
                    : 'Stopped before the answer started.',
                  streaming: false,
                  stopped: true,
                }
              : t,
          ),
        )
        return
      }
      const failed = t('threads.detail.command.report.failed')
      toast.error(reportTurnErrorMessage(error, failed))
      setTurns((prev) =>
        prev.map((turn) =>
          turn.id === assistantId
            ? { ...turn, text: failed, streaming: false, refused: true }
            : turn,
        ),
      )
    } finally {
      endTurn()
    }
  }

  const confirmIntakeDirectionRef = useRef(confirmIntakeDirection)
  confirmIntakeDirectionRef.current = confirmIntakeDirection
  const firedIntakeStartKeyRef = useRef<string | null>(null)

  function handleKeepTalking() {
    setIntakeStartUiDismissed(true)
    setIntakeDirection(null)
    setShowIntakeRetry(false)
    firedIntakeStartKeyRef.current = null
  }

  function handleArmedProceedWithoutMedia() {
    if (!blockedArmedAsk) {
      return
    }
    armedProceedWithoutStimulusRef.current = true
    void startArmedWorkflow(blockedArmedAsk)
  }

  /**
   * Latch before the request: a failed start (including one-active-run 409)
   * must not re-enter on the same "direction + audience exist" predicate.
   * Retry is a human click and calls `confirmIntakeDirection` without
   * clearing the latch.
   */
  useEffect(() => {
    if (!intakeStartKey) {
      firedIntakeStartKeyRef.current = null
    }
    if (!intakeStartKey || latchedStartKey !== intakeStartKey) {
      setShowIntakeRetry(false)
    }
  }, [intakeStartKey, latchedStartKey])

  useEffect(() => {
    setShowIntakeRetry(
      Boolean(loadArmedWorkflowState(token).state.latchedStartKey),
    )
  }, [token])

  useEffect(() => {
    if (!canAutoStartIntake || !intakeStartKey || isIntakeStartLatched) {
      return
    }
    if (firedIntakeStartKeyRef.current === intakeStartKey) {
      return
    }
    firedIntakeStartKeyRef.current = intakeStartKey
    latchIntakeStart(intakeStartKey)
    void confirmIntakeDirectionRef.current()
  }, [
    canAutoStartIntake,
    intakeStartKey,
    isIntakeStartLatched,
    latchIntakeStart,
  ])

  /**
   * ONE INTAKE TURN: the same endpoint every other turn on this page uses, in
   * `intake` mode.
   *
   * It renders as an ordinary exchange — the reader's message, then the
   * agent's prose — because that is what it is: the plan makes clarify
   * exchanges real thread turns, persisted, visible and metered, rather than
   * chrome bolted to the composer. The only thing the mode adds is the
   * terminal frame's tail, which says whether the conversation is still going
   * (`need_more`, and the composer stays armed) or has settled on a direction
   * (the confirm affordance appears).
   *
   * A missing tail is `need_more` by omission, which is the safe degrade: the
   * conversation continues and nothing spends.
   *
   * TWO PHASES, one turn. `scoping` is the conversation before a run exists.
   * `revision` is the same turn with an open gate's staged questions attached,
   * and the gate — not this page — decides what comes back is worth staging.
   * The handle is passed in rather than read from state at the end, because a
   * turn OUTLIVES the gate that started it: the captured copy is what turns a
   * gate that moved on into a notice instead of silence.
   *
   * The context ring is deliberately NOT fed from here. Intake runs on a fast
   * route with no retrieval and a small budget, and reporting its numbers as
   * the thread's would make the ring swing on a side conversation.
   */
  async function runIntakeTurn(
    rawMessage: string,
    revisionHandle?: GateRevisionHandle,
    options?: { kind?: 'report' },
  ) {
    const trimmed = rawMessage.trim()
    if (!token || !beginTurn()) {
      return
    }
    let shouldHandoff = false

    /* D12. The explicit option is for the tick on which `/report` arms, before
       `armedCommand` has caught up; every later turn of the same conversation
       reads the marker off the armed command. Twin-test resolves to undefined
       on both paths, which is what keeps `intake.kind` OFF its request. */
    const intakeKind = options?.kind ?? armedCommand?.intakeKind

    const assistantId = newId()
    const userTurnId = newId()
    setTurns((prev) => [
      ...prev,
      /* An empty opening message is normal under this mode and inserts no user
         turn: there is no question to show, and the server skips the empty row
         for the same reason. */
      ...(trimmed
        ? [
            {
              id: userTurnId,
              role: 'user' as const,
              text: trimmed,
              createdAt: new Date().toISOString(),
              commandDisplayName: armedWorkflow?.displayName,
              commandAudienceName: armedAudienceChipName ?? undefined,
            },
          ]
        : []),
      { id: assistantId, role: 'assistant' as const, text: '', streaming: true },
    ])

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null

      /* Read AT SEND TIME, never captured earlier: `readStagedQuestions` reads
         what the reviewer sees right now, so a card edited and saved between
         the gate opening and this Enter still travels with the message. */
      const intakePayload: {
        phase: 'scoping' | 'revision'
        questions?: Array<string>
        kind?: 'twin_test' | 'report'
        audienceName?: string
      } = revisionHandle
        ? { phase: 'revision', questions: revisionHandle.readStagedQuestions() }
        : { phase: 'scoping' }
      /* Added only when it is not the default. A twin-test intake request has
         to stay byte-identical to the one that ships today. */
      if (intakeKind && intakeKind !== 'twin_test') {
        intakePayload.kind = intakeKind
      }
      /* D4's resolver order: the confirm-card pick first, then whatever the
         page can name from what it already holds: the armed name, then the
         page-level audience list, then the remembered match. Report-kind
         intake never carries an audience name. Truncated client-side so a
         request never exceeds the DB column (`populations.name varchar(255)`)
         even if a caller ever hands this a longer name. A request with no
         bound audience must stay byte-identical to today's payload, so the
         key is added only when a non-empty name resolves. */
      if (intakePayload.kind !== 'report' && armedTemplate) {
        const resolvedAudienceName =
          armedAudienceName ||
          audienceNameForInputs(
            armedTemplate,
            armedInputs,
            pageAudiences,
            rememberedAudience,
          )
        if (resolvedAudienceName) {
          intakePayload.audienceName = resolvedAudienceName.slice(0, 255)
        }
      }
      const attachmentTokens = await collectAttachmentTokens()

      for await (const frame of assistantTurnStream(token, trimmed, {
        signal: controller.signal,
        mode: 'intake',
        intake: intakePayload,
        attachmentTokens,
      })) {
        if (frame.type === 'delta') {
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          throw new Error(frame.message)
        }
      }

      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const answer = done.text.trim() ? done.text : streamed
      /* Same reconciliation as the main sender: record the server's name for
         each half so run completion can recognise them later. An intake turn
         opened with an empty composer stored no user row, so its token is
         absent and there is nothing to patch. */
      const intakeUserToken = done.userMessageToken
      const intakeAssistantToken = done.assistantMessageToken
      /* Hoisted out of the map below because the i18n `t` is shadowed by that
         callback's own parameter. The twin-test line is left exactly as it
         shipped; only the report beat, which asks about something else
         entirely, gets its own. */
      const emptyIntakeAnswer =
        intakeKind === 'report'
          ? t('threads.detail.command.report.scoping')
          : 'Tell me what you want to test.'
      setTurns((prev) =>
        prev.map((t) =>
          t.id === userTurnId && intakeUserToken
            ? { ...t, serverToken: intakeUserToken }
            : t.id === assistantId
            ? {
                ...t,
                text: answer.trim() ? answer : emptyIntakeAnswer,
                streaming: false,
                ...(intakeAssistantToken
                  ? { serverToken: intakeAssistantToken }
                  : {}),
              }
            : t,
        ),
      )

      if (done.intake?.status === 'handoff' && trimmed) {
        /* Produce-intent: this brain cannot write. Disarm `/test` and replay
           the same sentence on the ordinary turn. The model named the status;
           we never inspect the user's words. */
        shouldHandoff = true
        clearArmedWorkflow()
        setIntakeStartUiDismissed(false)
        setIntakeDirection(null)
      } else if (revisionHandle) {
        /* Called on the CAPTURED handle even if the gate has since published
           null: that late call is what produces the "gate already closed"
           notice, and skipping it would let a moved gate fail silently. The
           outcome is informational and every message the reviewer reads is
           emitted inside the gate, so nothing is toasted here. */
        revisionHandle.applyRevision(done.intake, trimmed)
      } else {
        const settledAsk =
          done.intake?.status === 'direction'
            ? done.intake.ask?.trim()
            : undefined
        /* A `direction` with no ask is a tail that did not survive its own
           contract, so it is treated as one more question rather than as an
           agreement to spend. */
        setIntakeStartUiDismissed(false)
        setIntakeDirection(settledAsk ? settledAsk : null)
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      setThread(refreshed)
      setTitleDraft(refreshed.title)
      if (attachmentTokens.length > 0) {
        void refreshLivingArtifacts()
      }
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantId
              ? {
                  ...t,
                  text: t.text.trim()
                    ? t.text
                    : 'Stopped before the answer started.',
                  streaming: false,
                  stopped: true,
                }
              : t,
          ),
        )
        return
      }
      const messageText =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ??
        (error instanceof Error ? error.message : 'That turn failed')
      toast.error(messageText)
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: 'That turn did not finish. Try again.',
                streaming: false,
                refused: true,
              }
            : t,
        ),
      )
      /* Still armed. A failed intake turn is a turn to retry, not a reason to
         throw away the audience the reader already picked. */
    } finally {
      endTurn()
    }

    if (shouldHandoff) {
      await runAsk(trimmed, { existingAssistantId: assistantId })
    }
  }

  /**
   * Start the armed workflow in THIS thread.
   *
   * The typed sentence rides along as `message`: the hook maps it onto the
   * template's first `text` input, which is the same input whose label this
   * composer is already showing as its placeholder. Nothing is mapped here,
   * and the refusal for a template whose required inputs no composer can fill
   * (`audience`, `select`) is not repeated here either: both live inside
   * `useStartWorkflowRun`, once, and a second copy on this page would double
   * the toast and drift from the hook's rule the first time either changed.
   *
   * `startRun` never rejects; it resolves the created run, or null after any
   * refusal it already toasted. Only a real run is adopted: adoption is what
   * fills the pane without a reload (creating from this page is a same-path
   * navigation, so the thread-keyed fetch never re-fires) and it lights the
   * sidebar dot through the same path every other run update uses, so a
   * refusal lights nothing.
   */
  async function startArmedWorkflow(text: string) {
    if (!armedWorkflow || !token) {
      return
    }
    const trimmed = text.trim()
    const askKey = askInputKey(armedWorkflow)
    const runInputs = {
      ...armedInputs,
      ...(askKey ? { [askKey]: trimmed } : {}),
    }
    /* Insert the user bubble BEFORE the first await so a concurrently flushed
       `runAsk` (entry 2+) sees it in order (H7). If the stimulus gate then
       fails the optimistic turn is removed — same as if the reader never sent
       it, consistent with `setBlockedArmedAsk` today. */
    const optimisticTurnId = trimmed ? newId() : null
    if (optimisticTurnId) {
      setTurns((prev) => [
        ...prev,
        {
          id: optimisticTurnId,
          role: 'user' as const,
          text: trimmed,
          createdAt: new Date().toISOString(),
          commandDisplayName: armedWorkflow.displayName,
          /* The SAME resolver the composer chip reads (D4), stamped at send so
             the sent turn keeps both chips after the composer clears. */
          ...(armedAudienceChipName
            ? { commandAudienceName: armedAudienceChipName }
            : {}),
        },
      ])
    }
    if (
      !(await isStimulusGateSatisfiedAtRunStart({
        template: armedWorkflow,
        inputs: runInputs,
        ask: trimmed,
        proceedWithoutStimulus: armedProceedWithoutStimulusRef.current,
      }))
    ) {
      setBlockedArmedAsk(trimmed)
      if (optimisticTurnId) {
        setTurns((prev) => prev.filter((t) => t.id !== optimisticTurnId))
      }
      return
    }
    setBlockedArmedAsk(null)
    const startedRun = await startWorkflowRun({
      template: armedWorkflow,
      threadToken: token,
      message: text,
      inputs: runInputs,
      /* A thread action armed from the command menu (`/report`) is tagged the
         same way the menu's own start-now path tags it. */
      ...(armedCommand ? { trigger: 'thread_action' as const } : {}),
      ...workflowRunSegmentFieldsForSlug(commandSeed, armedWorkflow.slug),
    })
    if (startedRun) {
      armedProceedWithoutStimulusRef.current = false
      setBlockedArmedAsk(null)
      clearThreadComposerAttachments()
      /* Claim the run's ask id for the bubble already on screen. `createRun`
         persists the same sentence as a `workflow-run-ask:<token>` message,
         and `mergeRunCompletionAnnouncements` now pulls every `workflow-run-*`
         line in on the next settled-step refresh; without this the reader
         would watch their own sentence appear a second time mid-run. The
         askId is what that merge dedupes on, and it is also what
         `withRunCommandChip` and `userTurnCommandChipLabel` read. */
      if (optimisticTurnId) {
        const askId = workflowRunAskTurnAskId(startedRun.token)
        setTurns((prev) =>
          prev.map((turn) =>
            turn.id === optimisticTurnId ? { ...turn, askId } : turn,
          ),
        )
      }
      adoptRun(startedRun)
      /* Started is no longer armed: the same clear `confirmIntakeDirection`
         makes on success, so the textarea goes back to ordinary asking and
         the next Enter cannot post a second run into the server's
         one-active-run 409. The run itself stays visible as the locked chip
         in the header slot. */
      clearArmedWorkflow()
    } else if (optimisticTurnId) {
      /* Refused, and `startWorkflowRun` has already toasted why: take the
         bubble back down (nothing was persisted, so a reload would drop it
         anyway) and give the sentence back to the composer, where the reader
         can edit it and send again. The composer stays armed. */
      setTurns((prev) => prev.filter((turn) => turn.id !== optimisticTurnId))
      setComposerAskSeed((current) => ({
        text: trimmed,
        nonce: (current?.nonce ?? 0) + 1,
      }))
    }
  }

  /**
   * The context ring's escape hatch. Mints a fresh thread and lands on it,
   * mirroring `useHomeLabSubmit.ts:422-433` — the same `knowledgeThreadsApi.create`
   * call — rather than inventing a second way to start a thread. Language is
   * this thread's content language when set; if the parent never stored one,
   * fall back to `defaultContentLanguageFromUi()` so the child still gets a
   * session language (same default as a new thread). The scope carried over is
   * this thread's own: continuing a conversation into a new thread should not
   * quietly widen it back to the whole library.
   */
  async function continueInNewThread() {
    try {
      const created = await knowledgeThreadsApi.create({
        scope: {
          web: scope.web !== false,
          library: scope.library !== false,
          mode: scope.mode,
          documentTokens: scope.documentTokens,
          collectionTokens: scope.collectionTokens,
        },
        language: thread?.language || defaultContentLanguageFromUi(),
      })
      upsertSidebarThread(created)
      navigate(ROUTES.thread(created.token))
    } catch {
      toast.error(t('threads.detail.toast.startThreadFailed'))
    }
  }

  async function autosaveCreativeDraft(
    turn: LocalTurn,
    key: string,
    patch: { title: string; body: string },
  ) {
    const messageToken = turnMessageToken(turn)
    if (stoppedCreativeAutosaveRef.current.has(messageToken)) {
      return
    }
    setTurns((prev) =>
      prev.map((item) => {
        if (turnMessageToken(item) !== messageToken) {
          return item
        }
        if (!item.creativeDrafts) {
          return item
        }
        return {
          ...item,
          creativeDrafts: item.creativeDrafts.map((draft) =>
            draft.key === key
              ? { ...draft, title: patch.title, body: patch.body }
              : draft,
          ),
        }
      }),
    )
    setSavedDraftMessageTokens((prev) => ({ ...prev, [messageToken]: true }))
    const work = (async () => {
      try {
        const result = await knowledgeThreadsApi.patchCreativeDraft(
          token,
          messageToken,
          key,
          patch,
        )
        if (result?.creativeDrafts) {
          setTurns((prev) =>
            prev.map((item) =>
              turnMessageToken(item) === messageToken
                ? { ...item, creativeDrafts: result.creativeDrafts }
                : item,
            ),
          )
        }
      } catch (error: unknown) {
        if (isConflictStatus(error)) {
          stoppedCreativeAutosaveRef.current.add(messageToken)
          return
        }
        toast.error(confirmErrorMessage(error))
      }
    })()
    let byKey = pendingCreativeAutosavesRef.current.get(messageToken)
    if (!byKey) {
      byKey = new Map<string, Promise<void>>()
      pendingCreativeAutosavesRef.current.set(messageToken, byKey)
    }
    byKey.set(key, work)
    try {
      await work
    } finally {
      const current = pendingCreativeAutosavesRef.current.get(messageToken)
      if (current?.get(key) === work) {
        current.delete(key)
        if (current.size === 0) {
          pendingCreativeAutosavesRef.current.delete(messageToken)
        }
      }
    }
  }

  async function confirmCreativeDraftSet(turn: LocalTurn) {
    const messageToken = turnMessageToken(turn)
    const pendingByKey = pendingCreativeAutosavesRef.current.get(messageToken)
    if (pendingByKey && pendingByKey.size > 0) {
      const leftovers = Array.from(pendingByKey.values())
      for (const leftover of leftovers) {
        await leftover
      }
    }
    setConfirmingDraftMessageToken(messageToken)
    try {
      const result = await knowledgeThreadsApi.confirmCreativeDrafts(
        token,
        messageToken,
      )
      const artifacts = result?.artifacts ?? []
      await refreshLivingArtifacts()
      const confirmedAt = new Date().toISOString()
      setTurns((prev) =>
        prev.map((item) => {
          if (turnMessageToken(item) !== messageToken) {
            return item
          }
          const drafts = item.creativeDrafts ?? []
          return {
            ...item,
            creativeDrafts: stampCreativeDraftsFromConfirm({
              drafts,
              artifacts,
              creativeDrafts: result?.creativeDrafts,
              confirmedAt,
            }),
          }
        }),
      )
      stoppedCreativeAutosaveRef.current.add(messageToken)
    } catch (error: unknown) {
      if (isConflictStatus(error)) {
        stoppedCreativeAutosaveRef.current.add(messageToken)
      }
      toast.error(confirmErrorMessage(error))
    } finally {
      setConfirmingDraftMessageToken(null)
    }
  }

  async function cancelCreativeDraftSet(turn: LocalTurn) {
    const messageToken = turnMessageToken(turn)
    try {
      await knowledgeThreadsApi.clearCreativeDrafts(token, messageToken)
      setTurns((prev) =>
        prev.map((item) =>
          turnMessageToken(item) === messageToken
            ? { ...item, creativeDrafts: undefined }
            : item,
        ),
      )
      stoppedCreativeAutosaveRef.current.add(messageToken)
    } catch (error: unknown) {
      toast.error(confirmErrorMessage(error))
    }
  }

  /**
   * The reader answering a pending draft set with a bare "yes" / "confirm" /
   * "save". Same callback as the Confirm button. "go" is the plan door.
   */
  function handlePlainCreativeConfirm(rawMessage: string): boolean {
    if (!isPlainCreativeConfirm(rawMessage)) {
      return false
    }
    const target = resolveCreativeDraftConfirmTarget(turns)
    if (!target) {
      return false
    }
    const targetTurn = turns.find((item) => item.id === target.messageId)
    if (!targetTurn) {
      return false
    }
    setTurns((prev) => [
      ...prev,
      {
        id: newId(),
        role: 'user' as const,
        text: rawMessage.trim(),
        createdAt: new Date().toISOString(),
      },
    ])
    void confirmCreativeDraftSet(targetTurn)
    return true
  }

  /**
   * The reader answering the announcement's recommendation with a bare "go".
   *
   * The announcement promises that "go" starts the next step, so it has to,
   * or the copy is lying and the reader is left with a dialog as the only
   * way forward. This runs the SAME callbacks the plan card's own buttons
   * run: the conversational path and the card are two doors onto one action,
   * never two implementations of it.
   *
   * Returns true when it handled the message. A "go" typed inside a plan step
   * turn, or with anything else attached, is left alone: see `isPlainGo`.
   */
  function handlePlainGo(rawMessage: string): boolean {
    if (!isPlainGo(rawMessage)) {
      return false
    }
    const target = resolveGoTarget(turns, plansByMessageId)
    if (!target) {
      return false
    }
    /* The reader said it, so the thread shows it. Without this the column
       reads as the announcement followed straight by the next step's output,
       with nothing between them explaining why anything started. The turn is
       local only: agreeing is not a question, so there is no server message
       to reconcile it against, and a reload replays the thread from what the
       step actually did. */
    setTurns((prev) => [
      ...prev,
      {
        id: newId(),
        role: 'user' as const,
        text: rawMessage.trim(),
        createdAt: new Date().toISOString(),
      },
    ])
    if (target.mode === 'confirm') {
      applyPlanStepHandoff(target.messageId, confirmSoundsGood(target.messageId))
      return true
    }
    const plan = plansByMessageId[target.messageId]
    applyPlanStepHandoff(target.messageId, plan ? stepHandoffFrom(plan) : null)
    return true
  }

  async function runAsk(
    rawMessage: string,
    turnOptions?: {
      stepSkillSlug?: string
      planMessageId?: string
      existingAssistantId?: string
    },
  ) {
    const hostedAttachments = chatImagesToTurnAttachments(
      threadAttachmentsRef.current,
    )
    const trimmed =
      rawMessage.trim() ||
      (hostedAttachments.length > 0 ? ATTACHMENT_ONLY_ASK : '')
    if (!trimmed || askingRef.current || !token) return
    /* AFTER the busy guard, deliberately: agreeing to start something while a
       turn is still streaming would activate the plan and then bounce off the
       guard when the step tried to send, leaving the plan `active` with
       nothing running and no explanation. Only for a message the reader typed
       too: a step's own intent is dispatched through
       `turnOptions.planMessageId` and must never be re-read as an agreement. */
    if (!turnOptions?.planMessageId && handlePlainCreativeConfirm(rawMessage)) {
      return
    }
    if (!turnOptions?.planMessageId && handlePlainGo(rawMessage)) {
      return
    }
    /* Claimed HERE and not at the top: `handlePlainGo` dispatches the plan's
       own `runAsk` synchronously, and a guard claimed before it would refuse
       that nested turn. Nothing between the top check and this line awaits,
       so nothing can slip in either. */
    if (!beginTurn()) {
      return
    }
    const planMessageId = turnOptions?.planMessageId
    const stepSkillSlug = turnOptions?.stepSkillSlug
    const reuseAssistantId = turnOptions?.existingAssistantId
    let nextPlanHandoff: ThreadPlanStepHandoff | null = null
    if (planMessageId) {
      markCurrentRunning(planMessageId)
    }

    const assistantId = reuseAssistantId ?? newId()
    const userTurnId = reuseAssistantId ? null : newId()
    if (reuseAssistantId) {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? { ...t, text: '', streaming: true, refused: false }
            : t,
        ),
      )
    } else {
      const userTurn: LocalTurn = {
        id: userTurnId as string,
        role: 'user',
        text: trimmed,
        createdAt: new Date().toISOString(),
        ...(hostedAttachments.length > 0
          ? { attachments: hostedAttachments }
          : {}),
      }
      setTurns((prev) => [
        ...prev,
        userTurn,
        {
          id: assistantId,
          role: 'assistant' as const,
          text: '',
          streaming: true,
          /* Marks this turn as a plan step's own answer. Without it, a chain
             that stops at a workflow step leaves the newest turn looking like
             ordinary prose, and a plain "go" has nothing to bind to. */
          ...(planMessageId ? { planMessageId } : {}),
        },
      ])
    }
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    // A stopped turn keeps whatever text arrived, but must never keep its
    // streaming state — the caret would otherwise blink forever on a turn that
    // will receive nothing more. Citations only ship with the final frame, so a
    // stopped answer legitimately has none.
    const finalizeStopped = () => {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: t.text.trim()
                  ? t.text
                  : 'Stopped before the answer started.',
                streaming: false,
                stopped: true,
              }
            : t,
        ),
      )
    }

    const scopePayload = {
      web: scope.web !== false,
      library: scope.library !== false,
      ...(scope.mode === 'selected'
        ? {
            mode: 'selected' as const,
            documentTokens: scope.documentTokens,
            collectionTokens: scope.collectionTokens,
          }
        : {}),
      ...(scope.sourceKinds ? { sourceKinds: scope.sourceKinds } : {}),
      ...(scope.socialWindow ? { socialWindow: scope.socialWindow } : {}),
    }

    try {
      let streamed = ''
      let doneFrame: AssistantTurnDoneFrame | null = null
      const notes: Array<string> = []

      const attachmentTokens = await collectAttachmentTokens()
      for await (const frame of assistantTurnStream(token, trimmed, {
        signal: controller.signal,
        scope: scopePayload,
        effort,
        attachmentTokens,
        ...(stepSkillSlug ? { stepSkillSlug } : {}),
        ...(reuseAssistantId ? { skipUserMessage: true } : {}),
      })) {
        if (frame.type === 'delta') {
          streamed += frame.text
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? { ...t, text: streamed, streaming: true }
                : t,
            ),
          )
        } else if (frame.type === 'citations') {
          /* The citations frame lands BEFORE the first delta, so splitting it
             here is what lets the web cards arrive with the answer rather than
             only once it is finished. */
          const partitioned = partitionAssistantCitations(frame.items)
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    citations: partitioned.documentCitations,
                    webCitations: partitioned.webCitations,
                  }
                : t,
            ),
          )
        } else if (frame.type === 'judgment') {
          const note = frame.note.trim()
          if (note) {
            notes.push(note)
            setTurns((prev) =>
              prev.map((t) =>
                t.id === assistantId
                  ? { ...t, judgmentNotes: [...notes] }
                  : t,
              ),
            )
          }
        } else if (frame.type === 'tool_start') {
          /* The waiting line's only source of truth. Appended rather than
             replaced because legs run concurrently and each announces itself,
             so a turn running both arrives here twice before either ends. */
          const started = frame.tool
          const query =
            typeof frame.query === 'string' ? frame.query.trim() : ''
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: [
                      ...(t.activityLegs ?? []),
                      {
                        tool: started,
                        done: false,
                        ...(query ? { query } : {}),
                      },
                    ],
                  }
                : t,
            ),
          )
        } else if (frame.type === 'tool_end') {
          /* Matched by tool name, not by position: `tool_end` frames arrive in
             COMPLETION order (python joins the legs with `as_completed`), so
             the web leg can report back before the library leg that started
             first. `ok` is deliberately not read here. A failed leg has still
             stopped being something the reader is waiting on, and what it cost
             the answer is reported by the answer itself. */
          const ended = frame.tool
          if (isLivingArtifactMutationTool(ended)) {
            void refreshLivingArtifacts()
          }
          setTurns((prev) =>
            prev.map((t) =>
              t.id === assistantId
                ? {
                    ...t,
                    activityLegs: (t.activityLegs ?? []).map((leg) =>
                      leg.tool === ended ? { ...leg, done: true } : leg,
                    ),
                  }
                : t,
            ),
          )
        } else if (frame.type === 'done') {
          doneFrame = frame
        } else if (frame.type === 'error') {
          throw new Error(frame.message)
        }
      }

      // Narrowed into a const: `doneFrame` is assigned inside a loop, so the
      // closures below would otherwise see it as nullable again.
      const done = doneFrame
      if (!done) {
        throw new Error('The assistant stopped before finishing that turn.')
      }

      const answer = done.text.trim() ? done.text : streamed
      const finalCitations = partitionAssistantCitations(done.citations)
      // --- Agent C: attach plan to the turn + register with D's cursor hook ---
      const preparedSeed = done.preparedRun?.commandSeed
      const donePlan =
        done.plan && done.plan.cardEligible ? done.plan : undefined
      /* One `done` frame can carry BOTH a prepared run and a card-eligible
         plan, and python does not arbitrate between them. Opening the command
         dialog while a plan card is still waiting for Sounds good puts two
         gates on screen at once and lets the reader answer the second one
         first. The card is the only gate for a plan it is proposing: its
         Sounds good already opens the workflow step with the right audience
         through `confirmSoundsGood` -> `applyPlanStepHandoff`.

         `planMessageId` set means this turn is running INSIDE an already
         approved plan, so its prepared run must still open: that plan was
         agreed to, and this is the step being executed, not a new proposal. */
      const registersNewPlanCard = Boolean(donePlan && !planMessageId)
      if (preparedSeed?.slug && !registersNewPlanCard) {
        /* D3: a real ask (`planMessageId` null) hands its own words to
           `handleArmCommand` for when the armed template turns out to be
           the composer-owned-optional-ask kind. The approved-plan "go
           ahead" turn clears this so that path keeps reading `rest`. */
        pendingPreparedReaderAskRef.current =
          planMessageId == null
            ? {
                slug: preparedSeed.slug,
                message: trimmed,
                rest: preparedSeed.rest || trimmed,
              }
            : null
        seedPlanWorkflowStart(
          assistantId,
          preparedSeed.slug,
          preparedSeed.rest || trimmed,
          preparedSeed.audience ?? '',
          '',
          {
            populationFilter: preparedSeed.populationFilter,
            segmentLabel: preparedSeed.segmentLabel,
          },
        )
      }
      if (donePlan && !planMessageId) {
        const proposal = planTailToProposal(assistantId, donePlan)
        if (proposal) {
          registerProposal(proposal)
        }
      } else if (!preparedSeed?.slug && done.plan && !planMessageId) {
        const singleSlug = singleStepWorkflowSlug(done.plan)
        if (singleSlug) {
          const step = done.plan.steps[0]
          const stepWhy = step?.why?.trim() ?? ''
          seedPlanWorkflowStart(
            assistantId,
            singleSlug,
            trimmed || stepWhy,
            step?.inputs?.audience ?? '',
          )
        }
      }
      // --- end Agent C ---
      /* Both halves of this exchange learn the name the server gave them.
         The ids stay local (see `serverToken` on LocalTurn); this only adds
         the second name, so the next `listMessages` can tell that these two
         rows are already on screen. `userMessageToken` is absent, never
         null, when the turn stored no user row. */
      const userMessageToken = done.userMessageToken
      const assistantMessageToken = done.assistantMessageToken
      /* A turn whose whole answer travelled in the creative cards, a plan
         card or the exhibits leaves the prose blank on purpose. That is an
         answer, not a refusal, so neither the fallback line nor the refused
         tint applies to it. */
      const hasStructuredAnswer = Boolean(
        (done.creativeDrafts && done.creativeDrafts.length > 0) ||
          done.socialTrackerProposal ||
          donePlan ||
          (done.charts && done.charts.length > 0) ||
          (done.tables && done.tables.length > 0),
      )
      setTurns((prev) =>
        prev.map((t) => {
          if (userTurnId && t.id === userTurnId) {
            return userMessageToken
              ? { ...t, serverToken: userMessageToken }
              : t
          }
          if (t.id !== assistantId) {
            return t
          }
          return {
            ...t,
            text:
              answer.trim() || hasStructuredAnswer
                ? answer
                : 'No answer found in your sources.',
            streaming: false,
            refused: done.refused && !hasStructuredAnswer,
            gaps: done.gaps,
            citations: finalCitations.documentCitations,
            webCitations: finalCitations.webCitations,
            judgmentNotes: notes.length > 0 ? [...notes] : undefined,
            plan: donePlan,
            charts: asAnalystCharts(done.charts),
            tables: asAnalystTables(done.tables),
            ...(done.creativeDrafts && done.creativeDrafts.length > 0
              ? { creativeDrafts: done.creativeDrafts }
              : {}),
            ...(done.socialTrackerProposal
              ? { socialTrackerProposal: done.socialTrackerProposal }
              : {}),
            ...(assistantMessageToken
              ? { serverToken: assistantMessageToken }
              : {}),
          }
        }),
      )
      setContextUsage(done.contextUsage)
      setHistoryTruncated(done.truncated)
      if (done.switch || done.living || attachmentTokens.length > 0) {
        if (run?.token) {
          void mutate(['artifact-tips', run.token]).catch(() => undefined)
        }
        void refreshLivingArtifacts()
      }

      const refreshed = await knowledgeThreadsApi.get(token)
      setThread(refreshed)
      setTitleDraft(refreshed.title)
      clearThreadComposerAttachments()
      nextPlanHandoff = await refreshAfterTurn(planMessageId)
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        if (planMessageId) {
          markCurrentFailed(planMessageId)
        }
        finalizeStopped()
        return
      }
      /* The turn FAILS VISIBLY, and no second path answers in its place.
         This is the thread's only answer path and the only one that reports
         context usage, so a quiet re-ask elsewhere would hand the reader a
         different answer under the same question and call it a success. */
      const messageText =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ??
        (error instanceof Error ? error.message : 'That turn failed')
      toast.error(messageText)
      setTurns((prev) =>
        prev.map((t) =>
          t.id === assistantId
            ? {
                ...t,
                text: 'That turn did not finish. Try again.',
                streaming: false,
                refused: true,
              }
            : t,
        ),
      )
      if (planMessageId) {
        markCurrentFailed(planMessageId)
      }
    } finally {
      endTurn()
      /* An approved plan may keep answering itself IN THE THREAD: a skill
         step is prose in the column the reader is already reading, and
         Sounds good agreed to the whole playlist. A workflow step is not:
         it opens a dialog, and that is the reader's to open. So the chain
         carries on through skills and stops at the first workflow step,
         which then waits for Start or "go" like any other.

         Not absolute: a skill step whose own turn comes back with a
         `preparedRun` still opens that dialog through the done handler
         below. Decision 1 allows it inside an active plan, and the reader
         approved the playlist, but it is the one dialog this stop does not
         prevent. */
      if (nextPlanHandoff && nextPlanHandoff.kind !== 'workflow') {
        applyPlanStepHandoff(planMessageId ?? '', nextPlanHandoff)
      }
    }
  }

  async function handleScopeApply(next: KnowledgeScopeSelection) {
    setScope(next)
    if (!thread) return
    try {
      const updated = await knowledgeThreadsApi.update(thread.token, {
        scope: {
          web: next.web !== false,
          library: next.library !== false,
          mode: next.mode,
          documentTokens: next.documentTokens,
          collectionTokens: next.collectionTokens,
        },
      })
      setThread(updated)
    } catch {
      toast.error(t('chatChrome.updateScopeFailed'))
    }
  }

  if (loading || !thread) {
    return (
      <PageLayout variant="fullBleed">
        <PageContent
          className={THREAD_PAGE_SHELL_CLASS}
          data-testid="thread-detail-page"
        >
          <div className={THREAD_HEADER_CLASS}>
            <Skeleton className="h-5 w-64" />
          </div>
          <div
            className={cn(
              THREAD_BODY_ROW_CLASS,
              THREAD_PAGE_MAX_WIDTH_CLASS,
            )}
            data-testid="thread-body-row"
          >
            <ThreadDetailLoadingShell />
          </div>
        </PageContent>
      </PageLayout>
    )
  }

  const chipLabel = scopeChipLabel(scope)

  /* Turns before the current exchange. `turns.length` when the thread holds no
     question at all, which leaves the group empty rather than swallowing a
     column of answers that has no question to park. */
  const exchangeStart = lastQuestionIndex >= 0 ? lastQuestionIndex : turns.length
  /* Where the results sit. The run's own completion announcement first, the
     originating ask as the fallback for a run that predates announcements; on
     a live run, the run's own opening turn. Null leaves the pin at the end of
     the current exchange, which is what a just-finished run, and a live run
     with no opening turn of its own, want. */
  const pinAfterIndex = studyPinAfterIndex(
    turns,
    run?.token,
    originatingCommandTurnId,
    exchangeStart,
    { isLive: isRunActive },
  )

  /* The live run's cards render ABOVE the current exchange, so a message typed
     mid-run reads under them (D6). Two things follow: the pin in the historic
     loop is this run's, not a finished one's, and the exchange's viewport-tall
     parking floor has to go — it would push the message a screen below the
     cards it answers. */
  const livePinAboveExchange =
    isRunActive && pinAfterIndex !== null && pinAfterIndex < exchangeStart
  const appliedExchangeMinHeight = livePinAboveExchange ? 0 : exchangeMinHeight

  const threadRunGate = (
    <ThreadRunGate
      run={run}
      template={runTemplate}
      onSubmitGate={submitGate}
      onReviseStep={reviseStep}
      onCancelRun={cancelRun}
      onRevisionHandleChange={setGateRevisionHandle}
      onDockTweenChange={handleDockTweenChange}
      language={thread?.language}
      collapseSignal={gateCollapseSignal}
    />
  )

  /* The pin sits at the end of the current exchange on a live run, so the
     run's step-handoff and gate-decision lines would otherwise render above
     ALL of the run's cards in the turns column instead of between the cards
     of consecutive steps. Pull them out of the column here (both loops
     below skip `pinnedNoteTurnIds`) and hand the rendered nodes to
     `ThreadInlineArtifacts`, which interleaves them with the step groups.
     Legacy runs without step attribution (`run?.steps` empty) keep today's
     column placement. */
  const runStepNotes =
    (run?.steps?.length ?? 0) > 0
      ? currentAttemptWorkflowRunStepNotes(
          collectWorkflowRunStepNotes(turns, run?.token),
          run?.steps ?? [],
        )
      : []
  const pinnedNoteTurnIds = new Set(runStepNotes.map((note) => note.turn.id))
  const stepNoteNodes = runStepNotes.map((note) => ({
    key: note.turn.id,
    stepIndex: note.stepIndex,
    placement: note.placement,
    attempt: note.attempt,
    node: renderTurn(note.turn),
  }))

  const studyPin = (
    <>
      <ThreadRunResults
        run={run}
        className="mt-2"
        onSegmentByChange={setResultsSegmentBy}
        onExpandArtifact={openArtifactFullscreen}
      />
      <ThreadInlineArtifacts
        items={studyPinArtifacts}
        onExpand={openArtifactFullscreen}
        threadToken={token || null}
        steps={run?.steps ?? []}
        templateSlug={run?.templateSlug}
        language={thread?.language}
        stepNotes={stepNoteNodes}
      />
      {docksGateOnComposer ? null : threadRunGate}
    </>
  )

  /**
   * One turn, rendered the same wherever it sits in the column. Lifted out of
   * the JSX so the trailing exchange can be grouped without a second copy of
   * this: see `exchangeMinHeight`.
   */
  function questionBeforeTurn(turnId: string): string {
    const index = turns.findIndex((item) => item.id === turnId)
    for (let i = index - 1; i >= 0; i -= 1) {
      if (turns[i].role === 'user') {
        return turns[i].text
      }
    }
    return ''
  }

  function renderTurn(turn: LocalTurn) {
    if (turn.role === 'user') {
      const commandLabel = userTurnCommandChipLabel(turn, {
        lastQuestionId,
        originatingTurnId: originatingCommandTurnId,
        armedDisplayName: armedWorkflow?.displayName ?? null,
        runDisplayName: run?.templateDisplayName ?? null,
      })
      const audienceLabel = userTurnAudienceChipLabel(turn, {
        lastQuestionId,
        originatingTurnId: originatingCommandTurnId,
        armedAudienceName: armedAudienceChipName,
        runAudienceName: runAudienceChipName,
      })
      /* TWO CHIPS above the sent message, the same pair the composer showed
         (D2): the command, and the audience it was bound to. The audience chip
         renders only when a name resolves — never a blank second chip. */
      const commandChip = commandLabel ? (
        <span className="flex min-w-0 items-center gap-1.5">
          <ActiveWorkflowChip
            displayName={commandLabel}
            testId="thread-user-command-chip"
          />
          {audienceLabel ? (
            <ActiveWorkflowChip
              displayName={audienceLabel}
              icon="audience"
              testId="thread-user-command-chip-audience"
            />
          ) : null}
        </span>
      ) : null

      return (
        <div
          key={turn.id}
          ref={
            turn.id === lastQuestionId ? lastQuestionRef : null
          }
          className="ml-auto flex w-fit max-w-[80%] flex-col items-start gap-1.5"
          data-testid="thread-user-turn"
        >
          {commandChip}
          {turn.attachments && turn.attachments.length > 0 ? (
            <AttachmentTiles
              items={turn.attachments}
              testIdPrefix="thread-user-attachment"
            />
          ) : null}
          <div
            className="whitespace-pre-wrap break-words rounded-2xl bg-primary/10 px-4 py-3 text-base leading-[1.7] text-foreground"
            data-testid="thread-user-bubble"
          >
            {turn.text}
          </div>
        </div>
      )
    }

    const awaitingFirstToken = Boolean(
      turn.streaming && !turn.text.trim(),
    )

    return (
      <div
        key={turn.id}
        className="space-y-3"
        data-testid={
          isWorkflowRunCompletionAskId(turn.askId)
            ? 'thread-run-completion-announcement'
            : 'thread-assistant-bubble'
        }
      >
        {awaitingFirstToken ? (
          <ThreadActivityIndicator
            language={thread?.language}
            legs={turn.activityLegs}
          />
        ) : (
          <ThreadAnswerBody
            text={turn.text}
            citations={turn.citations}
            refused={turn.refused}
            streaming={turn.streaming}
            resolveLabel={citationLabel}
            onOpenCitation={openCitation}
            webCitations={turn.webCitations}
            judgmentNotes={turn.judgmentNotes}
            threadAssets={threadAssets}
            docsByToken={docsByToken}
          />
        )}
        {!turn.streaming && (turn.charts?.length || turn.tables?.length) ? (
          <ThreadFeaturedExhibits charts={turn.charts} tables={turn.tables} />
        ) : null}
        {/* Agent C: Suggested plan card — sibling of prose, never on composer.
            Callbacks go to D's useThreadPlanExecution; Sounds good / Skip
            auto-open ThreadCommandSeed. */}
        {!turn.streaming &&
        turn.creativeDrafts &&
        turn.creativeDrafts.length > 0 ? (
          <ThreadCreativeDraftSet
            drafts={turn.creativeDrafts}
            onAutosave={(key, patch) =>
              autosaveCreativeDraft(turn, key, patch)
            }
            onConfirm={() => confirmCreativeDraftSet(turn)}
            onCancel={() => void cancelCreativeDraftSet(turn)}
            onExpand={openArtifactFullscreen}
            isConfirming={
              confirmingDraftMessageToken === turnMessageToken(turn)
            }
            isDraftSaved={Boolean(
              savedDraftMessageTokens[turnMessageToken(turn)],
            )}
            threadAssets={threadAssets}
            docsByToken={docsByToken}
          />
        ) : null}
        {!turn.streaming &&
        hasSocialTrackers &&
        (turn.citations?.length || turn.webCitations?.length) ? (
          <ThreadTrackerProvenance citations={turn.citations} />
        ) : null}
        {!turn.streaming && turn.socialTrackerProposal ? (
          <ThreadSocialTrackerCard
            proposal={turn.socialTrackerProposal}
            threadToken={token}
            messageToken={turnMessageToken(turn)}
            question={questionBeforeTurn(turn.id)}
            onProposalChange={(proposal) =>
              setTurns((prev) =>
                prev.map((item) =>
                  item.id === turn.id
                    ? { ...item, socialTrackerProposal: proposal }
                    : item,
                ),
              )
            }
          />
        ) : null}
        {!turn.streaming && plansByMessageId[turn.id] ? (
          <ThreadPlanCard
            plan={plansByMessageId[turn.id]}
            onSoundsGood={() => {
              applyPlanStepHandoff(turn.id, confirmSoundsGood(turn.id))
            }}
            onDismiss={() => dismissPlan(turn.id)}
            onSkip={() => {
              applyPlanStepHandoff(turn.id, skipCurrent(turn.id))
            }}
            onRetry={() => {
              applyPlanStepHandoff(turn.id, retryCurrent(turn.id))
            }}
            onStartStep={(stepIndex) => {
              /* Same action as a typed "go", from the card instead of the
                 composer. The card only offers this on the step the plan is
                 actually on, and the index guard keeps it that way if that
                 ever changes. */
              const plan = plansByMessageId[turn.id]
              if (!plan || plan.currentIndex !== stepIndex) {
                return
              }
              applyPlanStepHandoff(turn.id, stepHandoffFrom(plan))
            }}
          />
        ) : null}
        {turn.stopped ? (
          <Text
            size="xs"
            color="muted"
            data-testid="thread-stopped-note"
          >
            Stopped — this answer is incomplete and has no
            sources.
          </Text>
        ) : null}
        {/* Quiet, collapsed, and last: the citations this answer
            could not tie to a sentence sit under the prose they
            belong to rather than in the rail, which only ever
            shows the newest grounded answer. Held back while the
            answer streams so the count does not churn as later
            sentences arrive and anchor more of them. */}
        {turn.streaming ? null : (
          <ThreadUnplacedSources
            indexes={unplacedByTurnId.get(turn.id)}
            citations={turn.citations}
            onOpen={openCitation}
          />
        )}
      </div>
    )
  }

  const showIntakeStartRow =
    Boolean(intakeDirection) &&
    !isStartingRun &&
    !isConfirmingIntake &&
    !intakeStartUiDismissed
  const intakeAskKey = armedWorkflow ? askInputKey(armedWorkflow) : undefined
  const showIntakeProceedWithoutMedia = Boolean(
    showIntakeStartRow &&
      armedWorkflow &&
      !hasStimulusGateSatisfied(
        armedWorkflow.inputs,
        {
          ...armedInputs,
          ...(intakeAskKey && intakeDirection
            ? { [intakeAskKey]: intakeDirection }
            : {}),
        },
        false,
        referencesStimulus,
      ),
  )
  const showArmedProceedWithoutMedia = Boolean(
    !showIntakeStartRow &&
      armedWorkflow &&
      !isIntakeArmed &&
      blockedArmedAsk &&
      !isStartingRun,
  )

  return (
    <QuickStudyCutPlotsContext.Provider value={cutPlotsSource}>
      {reportPageTitle && (
        <Helmet>
          <title>{reportPageTitle}</title>
        </Helmet>
      )}
      <PageLayout variant="fullBleed">
      <PageContent
        className={THREAD_PAGE_SHELL_CLASS}
        data-testid="thread-detail-page"
      >
        <div className={THREAD_HEADER_CLASS} data-testid="thread-header">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => void persistTitle()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                // Geist regular, not the display serif: this is a name in the
                // page chrome, not a title set on the page. `--font-display` is
                // for headings inside the reading column.
                className="w-full min-w-0 bg-transparent font-sans text-sm font-normal outline-none"
                data-testid="thread-title-input"
                aria-label={t('chatChrome.threadTitle')}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="thread-title-menu"
                    aria-label={t('chatChrome.threadDetails')}
                    className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel
                    className="font-normal text-xs text-muted-foreground"
                    data-testid="thread-owner-label"
                  >
                    {thread.ownerDisplayName
                      ? t('threads.detail.ownedBy', {
                          name: thread.ownerDisplayName,
                        })
                      : t('threads.detail.yourThread')}
                  </DropdownMenuLabel>
                  {/* Step 9: schedule siblings — recent runs for this schedule */}
                  {thread.scheduleSiblings && thread.scheduleSiblings.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel
                        className="font-normal text-xs text-muted-foreground"
                        data-testid="thread-schedule-runs-label"
                      >
                        {t('threads.detail.scheduleRecentRuns', 'Recent runs')}
                      </DropdownMenuLabel>
                      {thread.scheduleSiblings.map((sibling) => (
                        <DropdownMenuItem
                          key={sibling.token}
                          asChild
                          data-testid={`thread-schedule-sibling-${sibling.token}`}
                        >
                          <Link href={ROUTES.thread(sibling.token)}>
                            {sibling.title}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                data-testid="thread-context-chip"
                aria-label={chipLabel}
                className="inline-flex h-7 w-7 min-w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-background text-xs md:h-auto md:w-auto md:min-w-0 md:gap-1.5 md:overflow-visible md:px-2.5 md:py-1"
                onClick={() => setPickerOpen(true)}
              >
                <Folder
                  className="h-3.5 w-3.5 text-muted-foreground"
                  aria-hidden
                />
                <span className="hidden md:inline">{chipLabel}</span>
              </button>
              {/* One toggle for one drawer. The pane now stacks outputs,
                  assets, workflows, context, and progress, so the control is
                  the same expand glyph as the left sidebar rather than a
                  content label. */}
              {sourceTabs.length === 0 &&
              (hasRunPane ||
                hasArtifacts ||
                hasThreadAssets ||
                referenceGroups.length > 0) ? (
                <button
                  type="button"
                  data-testid="thread-sources-open"
                  title={railToggleLabel}
                  aria-label={railToggleLabel}
                  aria-expanded={railOpen}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    railOpen
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                  onClick={() => changeRailOpen(!railOpen)}
                >
                  <PanelRight className="h-[18px] w-[18px]" strokeWidth={1.5} />
                </button>
              ) : null}
            </div>
        </div>

        <div className={THREAD_PAGE_ROW_CLASS} data-testid="thread-page-row">
          <div
            className={cn(THREAD_BODY_ROW_CLASS, THREAD_PAGE_MAX_WIDTH_CLASS)}
            data-testid="thread-body-row"
          >
          <div className="flex min-w-0 flex-1 flex-col">
              <div className="relative flex min-h-0 w-full flex-1 flex-col">
              {/* Scroll-fade mask, the same one the Quick Study thread uses: it
                  softens the header/content seam without a hard divider or a
                  shadow. Page background color, solid for the top third then
                  fading to transparent, layered just above the scrolling thread
                  so content dissolves into the background as it meets the
                  header instead of being cut mid-glyph. Because it uses the
                  theme's own bg token it's seamless in dark mode too, and it
                  mirrors the composer wrap's `from-background` fade at the
                  bottom of the same column. Scoped INSIDE the reading column so
                  it can never wash over the sources rail, and held at zero
                  opacity until the thread is actually scrolled so it never
                  washes out a first question sitting at rest under the header. */}
              <div
                aria-hidden
                data-testid="thread-scroll-fade"
                className={cn(
                  'pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-background from-[33%] to-transparent transition-opacity duration-200',
                  conversationScrolled ? 'opacity-100' : 'opacity-0',
                )}
              />
              <div
                ref={conversationRef}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-28"
                style={
                  composerHeight > 0
                    ? /* The wrap's own `pt-8` is a transparent fade, so the
                         reservation clears that too: content must end above the
                         gradient, not inside it. */
                      { paddingBottom: `${composerHeight + 40}px` }
                    : undefined
                }
                data-testid="thread-conversation"
              >
                <div
                  className={cn('mx-auto w-full space-y-4', THREAD_COLUMN_CLASS)}
                  data-testid="thread-reading-column"
                >
                  {/* D3: guarded on loading and an active run too, not just an empty
                      `turns` array — a bare lobby start and F5 mid-run both have zero
                      turns for a beat, and this must not flash between "loaded" and
                      "the first turn actually rendered". */}
                  {turns.length === 0 && !loading && !isRunActive ? (
                    <Text
                      size="sm"
                      color="muted"
                      data-testid="thread-empty-placeholder"
                    >
                      {t('threads.detail.emptyThread')}
                    </Text>
                  ) : null}
                  {turns.slice(0, exchangeStart).map((turn, index) => (
                    <Fragment key={turn.id}>
                      {pinnedNoteTurnIds.has(turn.id) ? null : renderTurn(turn)}
                      {pinAfterIndex === index ? (
                        <div
                          className="space-y-2"
                          data-testid={
                            livePinAboveExchange
                              ? 'thread-live-study-pin'
                              : 'thread-historic-study-pin'
                          }
                        >
                          {studyPin}
                        </div>
                      ) : null}
                    </Fragment>
                  ))}

                  {/* THE CURRENT EXCHANGE: the newest question, its answer, and
                      — when this ask originated the latest run — the study
                      results and artifacts. Parking floor stays on this box.
                      A historic pin is rendered above, next to an earlier ask,
                      when the reader continued the thread after the run. The
                      box remains the reading column's last child either way. */}
                  <div
                    className="space-y-4"
                    data-testid="thread-current-exchange"
                    style={
                      appliedExchangeMinHeight > 0
                        ? { minHeight: appliedExchangeMinHeight }
                        : undefined
                    }
                  >
                    {turns.slice(exchangeStart).map((turn, offset) => (
                      <Fragment key={turn.id}>
                        {pinnedNoteTurnIds.has(turn.id) ? null : renderTurn(turn)}
                        {/* The announcement can land inside the current
                            exchange too, and a follow-up typed after it must
                            not drag the results below itself. */}
                        {pinAfterIndex === exchangeStart + offset ? (
                          <div className="space-y-2">{studyPin}</div>
                        ) : null}
                      </Fragment>
                    ))}

                    {/* Intake start / keep talking sit in the thread, same
                        place as creative Confirm — not inside the composer. */}
                    <ThreadIntakeStartActions
                      showIntakeRow={showIntakeStartRow}
                      showProceedWithoutMedia={showIntakeProceedWithoutMedia}
                      showRetry={showIntakeRetry}
                      showArmedProceed={showArmedProceedWithoutMedia}
                      onProceedWithoutMedia={() => {
                        armedProceedWithoutStimulusRef.current = true
                        void confirmIntakeDirection()
                      }}
                      onKeepTalking={handleKeepTalking}
                      onRetry={() => void confirmIntakeDirection()}
                      onArmedProceedWithoutMedia={
                        handleArmedProceedWithoutMedia
                      }
                    />

                    {pinAfterIndex === null ? studyPin : null}
                    {/* Busy line for the live step, UNDER the cards already
                        delivered: what is happening now reads after what has
                        happened, never above it. Prompt/report use the step
                        title; twin sampling keeps its own placeholder.
                        Prose-gate Continue sits with the artifacts above. */}
                    <ThreadRunActivityIndicator
                      run={run}
                      isStartingRun={isStartingRun}
                      language={thread?.language}
                    />
                  </div>
                  </div>
              </div>

              <div
                ref={measureComposerWrap}
                className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-background from-[33%] to-transparent pt-8 pb-[env(safe-area-inset-bottom)]"
                data-testid="thread-composer-wrap"
              >
                {isReadOnlyForViewer ? (
                  /* Somebody else's thread. The composer and its whole control
                     row are replaced rather than disabled: every control down
                     there — attach, commands, scope, effort, arm a workflow —
                     is a write this reader cannot make, and a row of dimmed
                     buttons invites the click that earns the 404 anyway. The
                     conversation above stays fully readable, which is the
                     entire point of sharing it. */
                  <div className={cn('mx-auto w-full', THREAD_COLUMN_CLASS)}>
                    <div
                      className="rounded-2xl border border-border/60 bg-twin-chat-surface px-5 py-4 text-sm text-muted-foreground"
                      data-testid="thread-composer-read-only"
                    >
                      {readOnlyThreadNotice(thread?.language)}
                    </div>
                  </div>
                ) : (
                <div className={cn('mx-auto w-full', THREAD_COLUMN_CLASS)}>
                  {/* Step 2 backstop: a persisted arm existed but failed
                      rehydrate validation. Storage-triggered only — messages
                      carry no mode, so turn history cannot infer this. */}
                  {wasArmedWorkflowDropped && !armedWorkflowSlug ? (
                    <div
                      className="mb-2 flex items-center justify-between gap-3"
                      data-testid="thread-armed-resume-offer"
                    >
                      <p className="text-xs text-muted-foreground">
                        {t('threads.detail.armedCleared')}
                      </p>
                      <button
                        type="button"
                        onClick={dismissArmedWorkflowDrop}
                        className="shrink-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                        data-testid="thread-armed-resume-dismiss"
                      >
                        {t('dismiss', { ns: 'common' })}
                      </button>
                    </div>
                  ) : null}
                  <ChatComposer
                    onSend={handleComposerSend}
                    seed={composerAskSeed}
                    isBusy={asking || isStartingRun}
                    onStop={handleStop}
                    onAddFiles={ingestThreadFiles}
                    sendBlockedHint={threadSendBlockedHint}
                    requireText={composerTiles.length === 0}
                    /* A revisable docked gate owns the box: a send is routed
                       into the gate as a revision, so the prompt says so.
                       Keyed on the handle and not on `docksGateOnComposer`,
                       because a non-owner sees the same card while their send
                       still falls through to an ordinary ask. Ahead of the
                       armed-workflow label, which is unreachable anyway while
                       a run is live. */
                    placeholder={
                      gateRevisionHandle
                        ? threadCopy.askChanges
                        : armedWorkflow
                          ? (armedWorkflow.inputs.find(
                              (input) => input.type === 'text',
                            )?.label ?? threadCopy.askNext)
                          : threadCopy.askNext
                    }
                    dataTestId="thread-composer"
                    textareaDataTestId="thread-composer-input"
                    /* Quiet and short: every control this page offers lives in
                       the row below, so the box holds a message and nothing
                       else, and a slab of drop shadow over the conversation
                       would be the loudest thing on the screen. */
                    flat
                    collapsible
                    commands={composerCommands}
                    onCommand={
                      armedWorkflowSlug ? undefined : handleTypedCommand
                    }
                    commandPreviewPlacement="above"
                    /* Questions preview is the only composer variation: same
                       surface as the revision textarea, hairline between
                       them. Scan / brief / report Continue stays in the
                       thread. `onRevisionHandleChange` still routes typed
                       sends from whichever mount is live. */
                    aboveSlot={docksGateOnComposer ? threadRunGate : undefined}
                    headerSlot={
                      /* The one thing that still belongs INSIDE the box: an
                         armed workflow rewrites what the textarea is for (the
                         placeholder becomes its own input label), so it reads
                         as a label on the field rather than as chrome beside
                         it. `undefined` when nothing is armed, so the box does
                         not pay for the header row's margin. */
                      run && isRunActive ? (
                        /* The LIVE run's own name, read off the run rather
                           than the armed state: a run underway is a fact, not
                           a choice to dismiss. Locked (no `onClear`), and so is
                           the audience chip beside it — the panel a running
                           study is drawn from is no more dismissible than the
                           study itself. */
                        <div className="flex min-w-0 items-center gap-2">
                          <ActiveWorkflowChip
                            displayName={run.templateDisplayName}
                          />
                          {runAudienceChipName ? (
                            <ActiveWorkflowChip
                              displayName={runAudienceChipName}
                              icon="audience"
                              testId="home-lab-active-workflow-audience"
                            />
                          ) : null}
                        </div>
                      ) : armedCommand && isReportIntakeArmed ? (
                        /* A native command has no template to name the chip
                           after, and without a chip there is no way out of the
                           scoping beat: the composer's own `/` list is off
                           while anything is armed. The command's label is the
                           name the reader picked, so it is the honest one. */
                        <ActiveWorkflowChip
                          displayName={armedCommand.label}
                          onClear={handleClearArmedWorkflow}
                        />
                      ) : armedWorkflow ? (
                        <div className="flex min-w-0 items-center gap-2">
                          {/* TWO CHIPS, NEVER ONE RENAMED CHIP (D2). The bound
                              audience used to REPLACE the command's name here,
                              so the reader saw one or the other and never both.
                              The command keeps its own name; the audience gets
                              a chip of its own, inert and without a dismiss
                              (D3) — the command's `×` clears the pair. */}
                          <ActiveWorkflowChip
                            displayName={armedWorkflow.displayName}
                            onClear={handleClearArmedWorkflow}
                          />
                          {armedAudienceChipName ? (
                            <ActiveWorkflowChip
                              displayName={armedAudienceChipName}
                              icon="audience"
                              testId="home-lab-active-workflow-audience"
                            />
                          ) : null}
                          {hasArmedStimulus ? (
                            <Text
                              size="sm"
                              color="muted"
                              as="span"
                              data-testid="thread-armed-stimulus-bound"
                            >
                              Stimulus bound
                            </Text>
                          ) : null}
                        </div>
                      ) : undefined
                    }
                  />

                  {/* THE CONTROL ROW, under the box and split in two: what the
                      reader can DO on the left, what the turn is CONFIGURED
                      with on the right. Nothing in here is part of the message
                      being typed, which is why none of it is inside the box any
                      more. */}
                  <div
                    /* ONE LINE, always: both clusters are single doors now,
                       so nothing in here is allowed to wrap onto a second row
                       under the composer. */
                    className="mt-2 flex w-full flex-nowrap items-center gap-1 md:gap-2"
                    data-testid="thread-composer-controls"
                  >
                    {/* Left: the actions, behind one door. The menu owns its
                        own feature gate, its own template lookup, its own
                        confirm screen and the whole twin-test audience picker,
                        and it adopts through the same `adoptRun` every other
                        start on this page uses, so a confirmed command fills
                        the pane and lights the sidebar dot without a reload.
                        `threadToken` is null until the thread record has
                        actually loaded, so nothing here can start a run against
                        a thread this page has not confirmed exists. */}
                    <div
                      className="flex min-w-0 items-center gap-2"
                      data-testid="thread-composer-attach"
                    >
                      <ChatFileUpload
                        images={composerTiles}
                        onImagesChange={setThreadAttachments}
                        onImagesMerge={setThreadAttachments}
                        onRemoveTile={handleRemoveComposerTile}
                        pdfText={threadPdfText}
                        pdfName={threadPdfName}
                        onPdfChange={handleThreadPdfChange}
                        maxImages={3}
                        disabled={asking || isStartingRun}
                        compact
                        acceptVideo
                        uploadHosted={uploadHostedChatStimulus}
                        pendingCount={threadFilePendingCount}
                        buttonClassName={composerTriggerClassName('external')}
                      />
                    </div>
                    <ThreadCommandMenu
                      threadToken={thread ? (token ?? null) : null}
                      activeRun={run}
                      cutGroups={cutGroupsFromRun(run)}
                      segmentByPrefill={resultsSegmentBy}
                      onRunStarted={handleCommandRunStarted}
                      onArm={handleArmCommand}
                      onArmReport={handleArmReportCommand}
                      rememberedAudience={rememberedAudience}
                      onRememberAudience={rememberAudience}
                      onForgetRememberedAudience={forgetRememberedAudience}
                      prelatchAudiences={audiencesPrelatched}
                      seed={commandSeed}
                      threadAssets={threadAssets}
                      composerAttachments={composerTiles}
                      appearance="external"
                      isSelected={
                        /* A native command has no template and no
                           `thread-action-` slug, so the surface test cannot
                           see it: `/report` paints Commands from its own
                           marker. */
                        isReportIntakeArmed
                          ? true
                          : armedWorkflowSlug
                          ? templateSurface(
                              workflowTemplates.find((template) => template.slug === armedWorkflowSlug) ?? {
                                slug: armedWorkflowSlug,
                              },
                            ) === 'thread'
                          : false
                      }
                    />

                    {/* Right: what this turn will read, how hard it will think,
                        which workflow it would run, then context at the far
                        right. `flex-1` + `justify-end` pins the last control
                        to the composer edge. */}
                    <div className="flex min-w-0 flex-1 items-center justify-end gap-0.5 md:gap-1">
                      <ScopeChip
                        label={chipLabel}
                        onClick={() => setPickerOpen(true)}
                        appearance="external"
                        testId="thread-scope-chip"
                      />
                      {/* Every thread turn IS an assistant turn, so unlike the
                          home composer, where effort only means something in
                          Ask, this control is always live here.
                          Frontend review BLOCKER 1: gated on the flag regardless, same
                          as the home composer's own copy of this control. */}
                      {assistantPlatformEnabled ? (
                        <EffortToggle
                          value={effort}
                          onChange={setEffort}
                          appearance="external"
                          className="shrink-0"
                        />
                      ) : null}
                      {/* The thread-side half of decision D3's workflow door.
                          Same component the home composer mounts, so a workflow
                          is armed one way on both surfaces.
                          Frontend review BLOCKER 1: gated on the flag, same reason as
                          the home composer's copy in `HomeLabStage.tsx`. */}
                      {assistantPlatformEnabled ? (
                        <WorkflowMenu
                          templates={workflowTemplates}
                          selectedSlug={armedWorkflowSlug}
                          /* Opens upward and hangs from its right edge: this
                             row is pinned to the bottom of the viewport, in
                             the right-hand cluster, so the home default
                             (below, start-anchored) would open off-screen and
                             overflow the column. */
                          placement="above"
                          align="end"
                          /* Same lock, same words as `ThreadCommandMenu`:
                             one active run per thread, one wording for it. */
                          disabled={isRunActive}
                          disabledReason={
                            isRunActive
                              ? t('threads.detail.runAlreadyActive')
                              : undefined
                          }
                          /* Arms by slug, and drops everything the OTHER door
                             held: a template record and an audience picked for
                             a different workflow have no business surviving
                             the switch. */
                          onSelect={handleArmSlug}
                          appearance="external"
                          className="shrink-0"
                        />
                      ) : null}
                      {assistantPlatformEnabled ? (
                        <ContextRing
                          contextUsage={contextUsage}
                          truncated={historyTruncated}
                          effort={effort}
                          onContinueInNewThread={continueInNewThread}
                          className="shrink-0"
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            <KnowledgeScopePicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              value={scope}
              onApply={(next) => void handleScopeApply(next)}
            />
          </div>
          </div>

          {/* One pane: stacked Outputs / Workflows / Context / Progress, an
              opened source, or one artifact expanded to its full body — each
              takes over the inside of the same drawer in turn. */}
          {hasRunPane ||
          hasArtifacts ||
          hasThreadAssets ||
          referenceGroups.length > 0 ||
          sourceTabs.length > 0 ? (
            <ThreadSourcesDrawer
              open={railSlidIn}
              onClose={() => {
                if (sourceTabs.length > 0) {
                  // Escape steps back to the sources list first: the drawer
                  // itself only closes from the header toggle.
                  setSourceTabs([])
                  setActiveSourceKey(null)
                  return
                }
                if (expandedArtifactKey !== null) {
                  // Same idea one layer in: back out of one expanded artifact
                  // to the Outputs list before the drawer closes, rather than
                  // losing the reader's place in the list entirely.
                  collapseExpandedArtifact()
                  return
                }
                changeRailOpen(false)
              }}
            >
              {/* The drawer does not change size or position when the reader
                  follows a citation or expands an artifact, so the swap inside
                  it should not snap either: keying the fade on which content
                  is showing restarts it on every move between layers. */}
              {railMounted ? (
                <div
                  key={
                    sourceTabs.length > 0
                      ? 'source'
                      : expandedArtifact
                        ? 'artifact-expanded'
                        : 'artifacts'
                  }
                  className="flex min-h-0 min-w-0 flex-1 flex-col motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
                >
                  {sourceTabs.length > 0 ? (
                    <ThreadSourcePanel
                      tabs={sourceTabs}
                      activeKey={activeSourceKey}
                      onCloseAll={() => {
                        setSourceTabs([])
                        setActiveSourceKey(null)
                      }}
                      onRelabel={relabelSourceTab}
                      onReportWrongPage={(citation) =>
                        void handleWrongPage(citation)
                      }
                    />
                  ) : expandedArtifact ? (
                    <ThreadArtifactExpandedView
                      item={expandedArtifact}
                      onClose={collapseExpandedArtifact}
                      onFullscreen={() =>
                        openArtifactFullscreen(expandedArtifact)
                      }
                      threadAssets={threadAssets}
                      docsByToken={docsByToken}
                      onOpenCitation={openCitation}
                      plots={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedPlots
                          : expandedArtifact.kind === 'report'
                            ? reportPlots
                            : undefined
                      }
                      quotes={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedQuotes
                          : expandedArtifact.kind === 'report'
                            ? reportQuotes
                            : undefined
                      }
                      populationId={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedPopulationId
                          : undefined
                      }
                      strips={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedStrips
                          : expandedArtifact.kind === 'report'
                            ? reportStrips
                            : undefined
                      }
                      decisionConfidence={
                        expandedArtifact.slug === STUDY_DOC_SLUG
                          ? focusedDecisionConfidence
                          : undefined
                      }
                      plotRegion="plot"
                    />
                  ) : (
                    <ThreadArtifactPane
                      artifacts={runArtifacts}
                      run={run}
                      template={runTemplate}
                      isLoading={runLoading}
                      error={runError}
                      livingIsLoading={livingArtifactsLoading}
                      livingError={livingArtifactsError}
                      pendingCreativeDraftCount={pendingCreativeDraftCount}
                      onRetryLiving={() => {
                        void refreshLivingArtifacts()
                      }}
                      showProgress={showRailProgress}
                      referenceGroups={referenceGroups}
                      docsByToken={docsByToken}
                      resolveLabel={citationLabel}
                      onOpenCitation={openCitation}
                      onRetryStep={retryStep}
                      onSubmitGate={submitGate}
                      onCancelRun={cancelRun}
                      onOpenArtifactFullscreen={openArtifactFullscreen}
                      language={thread?.language}
                      assets={threadAssets}
                      onRemoveAsset={handleRemoveAsset}
                      plots={studyDocPlots}
                      quotes={studyDocQuotes}
                      populationId={studyDocPopulationId}
                      strips={studyDocStrips}
                    />
                  )}
                </div>
              ) : null}
            </ThreadSourcesDrawer>
          ) : null}
        </div>
      </PageContent>
      {fullscreenArtifact ? (
        <ThreadArtifactFullscreenView
          item={fullscreenArtifact}
          onClose={closeArtifactFullscreen}
          threadAssets={threadAssets}
          docsByToken={docsByToken}
          onOpenCitation={openCitation}
          plots={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedPlots
              : fullscreenArtifact.kind === 'report'
                ? reportPlots
                : undefined
          }
          quotes={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedQuotes
              : fullscreenArtifact.kind === 'report'
                ? reportQuotes
                : undefined
          }
          populationId={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedPopulationId
              : undefined
          }
          strips={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedStrips
              : fullscreenArtifact.kind === 'report'
                ? reportStrips
                : undefined
          }
          decisionConfidence={
            fullscreenArtifact.slug === STUDY_DOC_SLUG
              ? focusedDecisionConfidence
              : undefined
          }
          plotRegion="plot"
        />
      ) : null}
      </PageLayout>
    </QuickStudyCutPlotsContext.Provider>
  )
}
